import { useState, useEffect, useRef, useCallback } from 'react';
import { createScanDetector } from '../../lib/scanDetector';
import { loadSalesConfig, readCachedSalesConfig } from '../../lib/salesConfigCache';
import { Search, Plus, Minus, Trash2, ShoppingCart, Camera, X, Check, CreditCard, Banknote, ChevronUp, ChevronDown, Wallet, Upload, Loader2, Clock, Printer, AlertTriangle, ShieldAlert, PackageX, Eye, EyeOff, Tag, Percent, Sprout, QrCode, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatPHP } from '../../lib/utils';
import PrintEngine from '../../lib/PrintEngine';
import PrintBridge from '../../lib/PrintBridge';
import TerminalPrintExtrasSheet, { hasExtras } from './TerminalPrintExtrasSheet';
import ReceiptQRDialog from '../../components/ReceiptQRDialog';
import SendToPrintModal from '../../components/SendToPrintModal';
import {
  getProducts, getCustomers, getPriceSchemes,
  addPendingSale, getPendingSaleCount, getInventoryItem, getBranchPrice,
} from '../../lib/offlineDB';
import { localTodayStr, nextCalendarDay } from '../../lib/dateFormat';
import { newEnvelopeId } from '../../lib/syncManager';
import CropCreditTypeDialog from '../../components/CropCreditTypeDialog';
import { invalidateBalanceCache } from '../../components/CustomerBalanceBadge';
import RequestSignatureDialog from '../../components/RequestSignatureDialog';
import OfflineCreditBypassDialog from '../../components/OfflineCreditBypassDialog';
import PriceMatchModal from '../../components/PriceMatchModal';
import { verifyOfflinePin } from '../../lib/offlineAuth';
import GlobalPriceBadge from '../../components/GlobalPriceBadge';
import CalcInput from '../../components/CalcInput';
import SaleReviewModal from '../../components/sales/SaleReviewModal';
import ScpwdDiscountModal from '../../components/ScpwdDiscountModal';
import { computeScpwd } from '../../utils/scpwd';

const COLLAPSE_THRESHOLD = 4; // show all if ≤ 4, add "More" toggle if > 4

function SchemeSwitcher({ schemes, activeScheme, onSwitch }) {
  const [expanded, setExpanded] = useState(false);
  const list = schemes.length > 0 ? schemes : [{ key: 'retail', name: 'Retail' }];
  const needsCollapse = list.length > COLLAPSE_THRESHOLD;
  const visible = needsCollapse && !expanded ? list.slice(0, COLLAPSE_THRESHOLD) : list;

  return (
    <div className="mb-2" data-testid="scheme-switcher">
      <div className="flex flex-wrap items-center gap-1.5">
        {visible.map(s => (
          <button key={s.key || s.id} onClick={() => onSwitch(s.key || s.id)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              activeScheme === (s.key || s.id)
                ? 'bg-[#1A4D2E] text-white'
                : 'bg-slate-100 text-slate-600 active:bg-slate-200'
            }`}
            data-testid={`scheme-${s.key || s.id}`}
          >
            <Tag size={10} className="inline mr-1" />{s.name}
          </button>
        ))}
        {needsCollapse && (
          <button
            onClick={() => setExpanded(v => !v)}
            className="px-3 py-1 rounded-full text-xs font-medium bg-slate-50 border border-slate-200 text-slate-500"
            data-testid="scheme-expand-btn"
          >
            {expanded ? '− Less' : `+${list.length - COLLAPSE_THRESHOLD} more`}
          </button>
        )}
      </div>
    </div>
  );
}


export default function TerminalSales({ api, session, isOnline, pendingCount, setPendingCount, syncVersion }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [activeScheme, setActiveScheme] = useState('retail');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [custSearch, setCustSearch] = useState('');
  const [showCustList, setShowCustList] = useState(false);
  const [cartExpanded, setCartExpanded] = useState(true);
  const searchRef = useRef(null);
  // Scanner mode (owner 2026-09-04): 'auto' = the detector watches the search
  // field for a scanner's fingerprint; 'off' = typing only, nothing is ever
  // interpreted as a scan (a scan while off just fills the box — the search
  // already matches barcodes, so it still finds the product). Per device.
  const SCANNER_MODE_KEY = 'agribooks.terminal.scanner_mode';
  const [scannerMode, setScannerMode] = useState(() => {
    try { return localStorage.getItem(SCANNER_MODE_KEY) === 'off' ? 'off' : 'auto'; } catch { return 'auto'; }
  });
  const scannerModeRef = useRef(scannerMode);
  useEffect(() => {
    scannerModeRef.current = scannerMode;
    try { localStorage.setItem(SCANNER_MODE_KEY, scannerMode); } catch { /* storage unavailable */ }
  }, [scannerMode]);
  const scannerRef = useRef(null);
  const scannerContainerRef = useRef(null);
  const lastScanRef = useRef({ barcode: '', time: 0 });
  const CAMERA_SCAN_COOLDOWN = 2000;
  const [lastSaleData, setLastSaleData] = useState(null); // for print prompt
  const [showPrintPrompt, setShowPrintPrompt] = useState(false);
  const [receiptQROpen, setReceiptQROpen] = useState(false);
  const [sendToPrintOpen, setSendToPrintOpen] = useState(false); // remote print for completed sale
  const [terminalSig, setTerminalSig] = useState({ open: false, invoice: null, preCommit: false }); // mandatory inline sig for credit/partial
  // Pre-commit signature (legal sequence: signature BEFORE invoice creation)
  const [pendingSigSession, setPendingSigSession] = useState(null); // { id, signature_url, bypass_method, signed_at, verification_token }
  // Phase 2: Offline credit-sale manager-PIN bypass
  const [offlineBypassDialog, setOfflineBypassDialog] = useState({ open: false, invoice: null });
  const [pendingOfflineBypass, setPendingOfflineBypass] = useState(null); // { method, by_name, reason, at }
  const saleIdRef = useRef(''); // stable per checkout intent → used as idempotency key
  const processingRef = useRef(false); // guards against rapid double-click re-entry
  const [businessInfo, setBusinessInfo] = useState({});
  // BIR receipt-workflow-aware wording. An official Invoice is issued AT CHECKOUT
  // only in DIRECT (auto-print) mode on a BIR branch; Staged/Smart-Role (and
  // non-BIR) produce an ORDER SLIP first → "Order Slip generated" / "Pay & Prepare Order".
  const [issuesInvoiceAtCheckout, setIssuesInvoiceAtCheckout] = useState(true);
  const [birSettings, setBirSettings] = useState({ bir_mode_enabled: false });
  const [taxConfig, setTaxConfig] = useState({ vat_rate: 12, scpwd_discount_percent: 20 });
  const [scpwd, setScpwd] = useState(null);
  const [scpwdModalOpen, setScpwdModalOpen] = useState(false);
  // Iter 243: Track last close date so terminal auto-bumps sale date to
  // next open day if today is already closed. Fetched from the same
  // /daily-close/unclosed-days endpoint the web POS uses. Falls back to
  // null on failure — we'll then use today and let backend reject.
  const [lastCloseDate, setLastCloseDate] = useState(null);
  // BIR dual-date confirm: once cashier acknowledges "sale_date=today,
  // record_date=tomorrow" for one sale this session, suppress the
  // confirm for subsequent sales so they don't get a popup per ring-up.
  // Resets on next mount / session change.
  const [dualDateAck, setDualDateAck] = useState(false);
  useEffect(() => {
    const bid = session?.branchId || session?.branch_id;
    if (!bid || !isOnline) return;
    api.get('/daily-close/unclosed-days', { params: { branch_id: bid } })
      .then(r => setLastCloseDate(r?.data?.last_close_date || null))
      .catch(() => {});
  }, [session?.branchId, session?.branch_id, isOnline, api, syncVersion]);
  // Insufficient stock override
  const [stockModal, setStockModal] = useState(false);
  const [insufficientItems, setInsufficientItems] = useState([]);
  const [pendingSaleData, setPendingSaleData] = useState(null);
  const [overridePin, setOverridePin] = useState('');
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideError, setOverrideError] = useState('');
  // HID Barcode Scanner (H10) — scan detection & quantity prompt
  const [scanQtyModal, setScanQtyModal] = useState(false);

  // Set of product IDs at the active branch with pending price review.
  // Used to render a tiny amber dot beside cart line items as a passive cue.
  const [pendingReviewIds, setPendingReviewIds] = useState(new Set());
  useEffect(() => {
    const bid = session?.branch_id;
    if (!bid || !isOnline) { setPendingReviewIds(new Set()); return; }
    api.get(`/inventory/pending-review-ids?branch_id=${bid}`)
      .then(r => setPendingReviewIds(new Set(r?.data?.product_ids || [])))
      .catch(() => {});
  }, [session?.branch_id, isOnline, api, syncVersion]);
  const [scanQtyProduct, setScanQtyProduct] = useState(null);
  const [scanQty, setScanQty] = useState('1');
  const autoAddProductsRef = useRef(new Set());
  const skipAllRef = useRef(false); // when true, all HID scans add 1 with no prompt this receipt
  const scanQtyModalOpenRef = useRef(false); // mirrors scanQtyModal — readable inside capture listener
  // lastScanRef already declared above (shared with camera scanner for unified 500ms dedup)
  // Receipt preview before printing
  const [receiptPreview, setReceiptPreview] = useState(null); // { html, type, format }
  const [printingCopies, setPrintingCopies] = useState(false);

  // Price scheme switching
  const [schemePinModal, setSchemePinModal] = useState(false);
  const [pendingScheme, setPendingScheme] = useState(null);
  const [schemePin, setSchemePin] = useState('');
  const [schemePinError, setSchemePinError] = useState('');
  const [schemePinLoading, setSchemePinLoading] = useState(false);

  // ── Line price editing (owner 2026-08-10) ─────────────────────────────────
  // Tap a cart line's price → modal with branch cost references → confirm.
  // Nothing persists to the catalog here — the change rides the sale and the
  // price-match gate at checkout (web parity) collects the manager PIN.
  const [priceEditItem, setPriceEditItem] = useState(null);  // cart item snapshot
  const [priceEditInput, setPriceEditInput] = useState('');

  // Price-match gate at checkout (web parity): changed prices need one
  // reason + scope + manager PIN for the whole receipt before the sale posts.
  const [priceMatchModal, setPriceMatchModal] = useState(false);
  const [priceMatchChanges, setPriceMatchChanges] = useState([]);
  const [priceMatchError, setPriceMatchError] = useState('');
  // Checkout args stashed while the modal is up; confirm resumes processSale
  // with the approval passed as an ARGUMENT (never via state — the web hit a
  // stale-closure bug doing that, see UnifiedSalesPage.js price-match notes).
  const pendingCheckoutRef = useRef(null);

  // ── Capital / cost visibility ────────────────────────────────────────────
  // Gated by admin/manager PIN via /api/products/cost-details (same as /sales-new).
  // Shows branch-specific cost (branch_prices.cost_price → global fallback).
  const [capitalShown, setCapitalShown]         = useState(false);
  const [capitalPin, setCapitalPin]             = useState(''); // retained after validation — passed on lazy re-fetches
  const [capitalPinModal, setCapitalPinModal]   = useState(false);
  const [capitalPinInput, setCapitalPinInput]   = useState('');
  const [capitalPinError, setCapitalPinError]   = useState('');
  const [capitalPinLoading, setCapitalPinLoading] = useState(false);
  const [costMap, setCostMap]                   = useState({}); // { product_id: {effective_cost, last_purchase, moving_average} }
  const capitalPinRef                           = useRef(null);

  const branchId = session?.branchId || session?.branch_id || '';

  const fetchCostMap = useCallback(async (ids, pin) => {
    if (!ids?.length || !branchId || !isOnline) return;
    try {
      const body = { branch_id: branchId, product_ids: ids };
      if (pin) body.pin = pin;
      const r = await api.post('/products/cost-details', body);
      setCostMap(prev => ({ ...prev, ...(r.data?.costs || {}) }));
    } catch { /* non-critical */ }
  }, [api, branchId, isOnline]);

  const submitCapitalPin = async () => {
    if (!capitalPinInput) { setCapitalPinError('PIN required'); return; }
    setCapitalPinLoading(true); setCapitalPinError('');
    try {
      await api.post('/products/cost-details', {
        branch_id: branchId,
        product_ids: ['__validate__'],
        pin: capitalPinInput,
      });
      // PIN accepted — store it for lazy re-fetches (so newly-added items also get MA/LP)
      const validatedPin = capitalPinInput;
      setCapitalPin(validatedPin);
      // Fetch costs for all currently visible products
      const visibleIds = [
        ...results.map(p => p.id),
        ...cart.map(c => c.product_id),
      ];
      await fetchCostMap(visibleIds, validatedPin);
      setCapitalShown(true);
      setCapitalPinModal(false);
      setCapitalPinInput('');
    } catch (e) {
      setCapitalPinError(e.response?.data?.detail || 'Invalid PIN — admin or manager required');
    } finally {
      setCapitalPinLoading(false);
    }
  };

  const hideCapital = () => {
    setCapitalShown(false);
    setCapitalPin('');   // clear stored PIN on hide
    setCostMap({});
  };

  // Lazy-fetch costs when new products appear in search results or cart.
  // Pass the stored capitalPin so non-admin users get MA/LP for items added
  // AFTER the initial capital reveal (previously pin was missing → MA=0/LP=0).
  useEffect(() => {
    if (!capitalShown) return;
    const missing = [];
    results.forEach(p => { if (p.id && !(p.id in costMap)) missing.push(p.id); });
    cart.forEach(c => { if (c.product_id && !(c.product_id in costMap)) missing.push(c.product_id); });
    if (missing.length) fetchCostMap(missing, capitalPin || undefined);
  }, [capitalShown, results, cart, costMap, fetchCostMap, capitalPin]);

  // Crop Credit type selection
  const [cropTypeDialog, setCropTypeDialog] = useState(false);
  const [cropCreditConfig, setCropCreditConfig] = useState(null);

  // Total discount
  const [discountInput, setDiscountInput] = useState('');
  // Per-line discount mode shared with bill-level discount input.
  // 'amount' = ₱ off per unit (default — most common in PH retail).
  // 'percent' = % off. Toggled via the pill at top of the cart panel.
  const [discountMode, setDiscountMode] = useState('amount');
  // Which cart line's inline discount editor is currently open. null = none.
  const [discEditorFor, setDiscEditorFor] = useState(null);
  // Sale review modal — pops up before processSale if any discount or below-cost line.
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSigSession, setReviewSigSession] = useState(null);
  const [marginWarningAccepted, setMarginWarningAccepted] = useState(false);
  // Starts from what the SERVER last told this device, never from a bare default:
  // a missing `vat_enabled` reads as VAT ON, which turned a non-VAT business into a
  // VAT one whenever the terminal was offline (owner report 2026-09-21).
  const [salesConfig, setSalesConfig] = useState(() => ({
    min_margin_percent: 1, margin_warning_enabled: true,
    ...(readCachedSalesConfig(session?.organizationId) || {}),
  }));

  // Keep ref in sync with state so the capture-phase listener can read it without stale closure
  useEffect(() => { scanQtyModalOpenRef.current = scanQtyModal; }, [scanQtyModal]);

  // Load cached data — re-reads from IndexedDB when syncVersion changes (background sync completed)
  useEffect(() => {
    (async () => {
      const [prods, custs, schs] = await Promise.all([getProducts(), getCustomers(), getPriceSchemes()]);
      setProducts(prods);
      setCustomers(custs);
      setSchemes(schs);
    })();
    api.get('/settings/business-info').then(r => setBusinessInfo(r.data || {})).catch(() => {});
    loadSalesConfig(api, session?.organizationId).then(got => { if (got) setSalesConfig(got.config); });
    api.get('/settings/tax-config').then(r => setTaxConfig(r.data || { vat_rate: 12, scpwd_discount_percent: 20 })).catch(() => {});
    if (session?.branchId) {
      api.get('/bir/settings', { params: { branch_id: session.branchId } })
        .then(r => {
          setBirSettings(r.data || { bir_mode_enabled: false });
          setIssuesInvoiceAtCheckout(!!r.data?.bir_mode_enabled && (r.data?.receipt_workflow || 'staged') === 'direct');
        })
        .catch(() => {});
    }
  }, [syncVersion, isOnline]); // eslint-disable-line

  // ── Price Scheme Switching ────────────────────────────────────────────────
  const switchScheme = useCallback((schemeKey) => {
    if (schemeKey === activeScheme) return;
    // Retail is always free. Non-retail requires PIN.
    if (schemeKey === 'retail') {
      applyScheme(schemeKey);
    } else {
      setPendingScheme(schemeKey);
      setSchemePin('');
      setSchemePinError('');
      setSchemePinModal(true);
    }
  }, [activeScheme]);

  const applyScheme = useCallback((schemeKey) => {
    setActiveScheme(schemeKey);
    // Recalculate all cart prices for the new scheme
    setCart(prev => prev.map(item => {
      const product = products.find(p => p.id === item.product_id);
      const newPrice = product?.prices?.[schemeKey] ?? item.price;
      return { ...item, price: newPrice, original_price: newPrice, total: item.quantity * newPrice };
    }));
    setDiscountInput('');
    setMarginWarningAccepted(false);
    toast.success(`Switched to ${schemeKey} pricing`);
  }, [products]);

  const confirmSchemePin = async () => {
    if (!schemePin.trim() || !pendingScheme) return;
    setSchemePinLoading(true);
    setSchemePinError('');
    try {
      await api.post('/verify/verify-pin-action', {
        pin: schemePin.trim(),
        action: 'terminal_wholesale_switch',
        branch_id: session.branchId,
      });
      applyScheme(pendingScheme);
      setSchemePinModal(false);
      setPendingScheme(null);
      setSchemePin('');
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSchemePinError(typeof d === 'string' ? d : 'Invalid PIN');
    }
    setSchemePinLoading(false);
  };

  // Search products — debounced 150ms + uses pre-built search_blob
  // (server-merged on /sync/pos-data) for fast filtering at 5K+ SKUs.
  // Falls back to multi-field lowercase concat if search_blob is missing
  // (older cache that hasn't synced since the upgrade).
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 150);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!debouncedSearch.trim()) { setResults([]); return; }
    const q = debouncedSearch.toLowerCase();
    const filtered = products.filter(p => {
      if (p.search_blob) return p.search_blob.includes(q);
      // Legacy fallback
      return (p.name || '').toLowerCase().includes(q)
        || (p.sku || '').toLowerCase().includes(q)
        || (p.barcode || '').includes(debouncedSearch);
    }).slice(0, 20);
    setResults(filtered);
  }, [debouncedSearch, products]);

  const getPrice = (product) => product.prices?.[activeScheme] ?? 0;

  // Per-line discount: compute net unit price & line total given a cart item.
  // Backwards-compatible — items without `discount_value` behave exactly as
  // before (net = price). Cap at 0 so a 110% discount can't go negative.
  const computeNetUnit = (item) => {
    const dv = parseFloat(item.discount_value) || 0;
    if (dv <= 0) return item.price;
    if (item.discount_type === 'percent') return Math.max(0, item.price * (1 - dv / 100));
    return Math.max(0, item.price - dv); // 'amount' = ₱ off per unit
  };
  const computeLineTotal = (item) => round2(computeNetUnit(item) * (item.quantity || 0));
  function round2(n) { return Math.round((+n || 0) * 100) / 100; }
  // True if line's NET unit price is at/below capital — triggers admin-PIN gate.
  const isBelowCost = (item) => {
    const cap = parseFloat(item.effective_capital ?? item.cost_price) || 0;
    if (cap <= 0) return false;
    return computeNetUnit(item) <= cap;
  };

  // Display name of the active price scheme ("Retail", "Wholesale", custom…)
  const activeSchemeName =
    schemes.find(s => s.key === activeScheme)?.name
    || (activeScheme ? activeScheme.charAt(0).toUpperCase() + activeScheme.slice(1) : 'Retail');

  const openPriceEditor = (item) => {
    setPriceEditItem(item);
    setPriceEditInput(String(item.price ?? ''));
  };

  const confirmPriceEdit = () => {
    if (!priceEditItem) return;
    // Same comma guard as CalcInput — "2,000" must not become 2.
    const newPrice = round2(parseFloat(String(priceEditInput).replace(/[,₱\s]/g, '')) || 0);
    if (newPrice <= 0) { toast.error('Enter a valid price'); return; }
    setCart(prev => prev.map(c => {
      if (c.product_id !== priceEditItem.product_id) return c;
      const updated = { ...c, price: newPrice };
      return { ...updated, total: computeLineTotal(updated) };
    }));
    setPriceEditItem(null);
    setPriceEditInput('');
  };

  // Web-parity change detection (UnifiedSalesPage computePriceChanges):
  // repacks are skipped (pinned to retail, managed in Repack Pricing);
  // original_price 0 counts as first-time pricing.
  const computePriceChanges = () => {
    const changes = [];
    for (const c of cart) {
      if (c.is_repack) continue;
      const orig = round2(parseFloat(c.original_price) || 0);
      const now = round2(parseFloat(c.price) || 0);
      if (now <= 0) continue;
      if (orig === 0 || Math.abs(now - orig) > 0.001) {
        changes.push({
          product_id: c.product_id, product_name: c.product_name,
          old_price: orig, new_price: now, scheme: activeScheme,
        });
      }
    }
    return changes;
  };

  const addToCart = useCallback((product) => {
    if (product?.disabled_at_branch) {
      toast.error(`${product.name} is disabled at this branch`, { duration: 2500 });
      return;
    }
    const price = product.prices?.[activeScheme] ?? 0;
    setCart(prev => {
      const existing = prev.find(c => c.product_id === product.id);
      if (existing) {
        return prev.map(c => c.product_id === product.id
          ? { ...c, quantity: c.quantity + 1, total: computeLineTotal({ ...c, quantity: c.quantity + 1 }) }
          : c
        );
      }
      return [...prev, {
        product_id: product.id, product_name: product.name, sku: product.sku,
        // Same drop as the desk lane had: the terminal cart never carried the
        // product's description, so a repack sold here saved without its
        // "Repack from ..." line and a service line lost the only record of
        // what the job was.
        description: product.description || '',
        price, quantity: 1, total: price, unit: product.unit, is_repack: product.is_repack,
        category: product.category || '',
        cost_price: product.cost_price || 0,
        effective_capital: product.effective_capital || product.cost_price || 0,
        capital_method: product.capital_method || 'manual',
        original_price: price,
        discount_type: 'amount', discount_value: 0,
      }];
    });
    setSearch('');
    setResults([]);
    toast.success(product.name, { duration: 1500 });
  }, [activeScheme]);

  // ── HID Barcode Scanner Detection ──────────────────────────────────────────
  // Detection lives in lib/scanDetector.js (pure, unit-tested with real
  // timings). Owner 2026-09-04: the old inline detector called two keys
  // < 50 ms apart a "scan" and swallowed everything after — fast typing
  // lost letters or got "Barcode not found". Keys are never blocked now.

  const processBarcodeScan = useCallback((barcode) => {
    // Dedup: ignore same barcode scanned within 500ms (scanner bounce protection)
    const now = Date.now();
    if (barcode === lastScanRef.current.barcode && now - lastScanRef.current.time < 500) return;
    lastScanRef.current = { barcode, time: now };

    const product = products.find(p => p.barcode === barcode);
    if (!product) {
      toast.error(`Barcode "${barcode}" not found. Scan again or add product on computer.`, { duration: 3000 });
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      searchRef.current?.focus();
      return;
    }
    // Skip-all mode — silent 1-per-scan for everything this receipt
    if (skipAllRef.current) {
      addToCart(product);
      if (navigator.vibrate) navigator.vibrate(100);
      searchRef.current?.focus();
      return;
    }
    // Product already marked for auto-add this receipt
    if (autoAddProductsRef.current.has(product.id)) {
      addToCart(product);
      if (navigator.vibrate) navigator.vibrate(100);
      searchRef.current?.focus();
      return;
    }
    // First scan of this product — show quantity prompt
    setScanQtyProduct(product);
    setScanQty('1');
    setScanQtyModal(true);
  }, [products, addToCart]);

  const handleSearchKeyDown = useCallback((e) => {
    // Enter on search field picks the first result
    if (e.key === 'Enter' && results.length > 0) addToCart(results[0]);
  }, [results, addToCart]);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  // mode: 'add' (use qty input) | 'skip_product' (auto+1 this product) | 'skip_all' (auto+1 everything)
  const handleScanQtyConfirm = useCallback((mode = 'add') => {
    if (!scanQtyProduct) return;
    const qty = (mode === 'add') ? Math.max(1, parseInt(scanQty) || 1) : 1;
    if (mode === 'skip_product') autoAddProductsRef.current.add(scanQtyProduct.id);
    if (mode === 'skip_all') skipAllRef.current = true;
    const price = scanQtyProduct.prices?.[activeScheme] ?? 0;
    setCart(prev => {
      const existing = prev.find(c => c.product_id === scanQtyProduct.id);
      if (existing) {
        const newQty = existing.quantity + qty;
        const next = { ...existing, quantity: newQty };
        return prev.map(c => c.product_id === scanQtyProduct.id
          ? { ...next, total: computeLineTotal(next) } : c);
      }
      return [...prev, {
        product_id: scanQtyProduct.id, product_name: scanQtyProduct.name, sku: scanQtyProduct.sku,
        price, quantity: qty, total: price * qty, unit: scanQtyProduct.unit, is_repack: scanQtyProduct.is_repack,
        cost_price: scanQtyProduct.cost_price || 0,
        effective_capital: scanQtyProduct.effective_capital || scanQtyProduct.cost_price || 0,
        capital_method: scanQtyProduct.capital_method || 'manual',
        original_price: price,
        discount_type: 'amount', discount_value: 0,
      }];
    });
    toast.success(`${scanQtyProduct.name} x${qty}`, { duration: 1500 });
    if (navigator.vibrate) navigator.vibrate(100);
    setScanQtyModal(false);
    setScanQtyProduct(null);
    setTimeout(() => searchRef.current?.focus(), 100);
  }, [scanQtyProduct, scanQty, activeScheme]);

  const updateQty = (productId, delta) => {
    setCart(prev => prev.map(c => {
      if (c.product_id !== productId) return c;
      const newQty = Math.max(1, c.quantity + delta);
      const next = { ...c, quantity: newQty };
      return { ...next, total: computeLineTotal(next) };
    }));
  };

  // Per-line discount setter — value can be empty string (clears discount).
  // Type follows the current global `discountMode` so cashiers see one source
  // of truth at the top of the cart (₱ vs %).
  const setLineDiscount = (productId, value) => {
    setCart(prev => prev.map(c => {
      if (c.product_id !== productId) return c;
      const v = value === '' || value == null ? 0 : Math.max(0, parseFloat(value) || 0);
      const next = { ...c, discount_type: discountMode === 'percent' ? 'percent' : 'amount', discount_value: v };
      return { ...next, total: computeLineTotal(next) };
    }));
  };

  const removeItem = (productId) => setCart(prev => prev.filter(c => c.product_id !== productId));
  const clearCart = () => { setCart([]); setSelectedCustomer(null); setCustSearch(''); autoAddProductsRef.current.clear(); skipAllRef.current = false; setDiscountInput(''); setMarginWarningAccepted(false); setScpwd(null); };

  // Receipt preview → print copies
  const handleShowReceiptPreview = (format) => {
    if (!lastSaleData) return;
    const type = PrintEngine.getDocType(lastSaleData);
    const docCode = lastSaleData.doc_code || lastSaleData.invoice_number || '';
    const html = PrintEngine.generateHtml({ type, data: lastSaleData, format, businessInfo, docCode });
    // Close "Sale Complete" dialog first so Android WebView doesn't stack dialogs
    setShowPrintPrompt(false);
    setReceiptPreview({ html, type, format });
  };

  // Extra copies of a receipt that carries a description or a note ask what to
  // include, the same as the desk lane. The FIRST receipt at checkout is not
  // asked — it prints everything, because the cashier has not left the sale yet.
  const [copiesExtrasAsk, setCopiesExtrasAsk] = useState(null);
  const handlePrintCopies = (copies) => {
    if (!receiptPreview || !lastSaleData) return;
    if (hasExtras(lastSaleData)) { setCopiesExtrasAsk(copies); return; }
    runPrintCopies(copies, null);
  };
  const runPrintCopies = async (copies, extras) => {
    if (!receiptPreview || !lastSaleData) return;
    setPrintingCopies(true);
    // Inter-copy gap is sourced from PrintBridge so the timing automatically
    // adapts to the native plugin's capability (legacy fire-and-forget → uses
    // an estimate; post-Cursor-fix awaitsCompletion → just the 2 s cut buffer).
    try {
      for (let i = 0; i < copies; i++) {
        await PrintBridge.print({ type: receiptPreview.type, data: lastSaleData, format: receiptPreview.format, businessInfo, docCode: lastSaleData.doc_code || lastSaleData.invoice_number || '', extras });
        if (i < copies - 1) {
          const gap = await PrintBridge.interCopyGapMs({ data: lastSaleData, format: receiptPreview.format });
          await new Promise(r => setTimeout(r, gap));
        }
      }
      toast.success(`Printed ${copies} ${copies === 1 ? 'copy' : 'copies'}`);
    } catch (err) {
      toast.error(`Print failed: ${err.message || 'Unknown error'}`);
    }
    setPrintingCopies(false);
    setReceiptPreview(null);
    setShowPrintPrompt(false);
    setLastSaleData(null);
  };

  const subtotal = cart.reduce((s, c) => s + c.total, 0);
  const discountAmount = discountMode === 'percent'
    ? subtotal * (parseFloat(discountInput) || 0) / 100
    : (parseFloat(discountInput) || 0);

  // ── Senior Citizen / PWD discount preview (mirrors backend math) ──────────
  const scpwdAvailable = !!birSettings?.senior_citizen_discount_enabled;
  const vatEnabled = salesConfig?.vat_enabled !== false;
  const scpwdLines = cart.map(c => ({ total: c.total, vat_class: !vatEnabled ? 'vat_exempt' : (c.vat_class || 'vatable') }));
  const scpwdCalc = (scpwd && scpwdLines.length)
    ? computeScpwd(scpwdLines, { vatRate: taxConfig.vat_rate, scpwdPct: taxConfig.scpwd_discount_percent, isVatBranch: birSettings?.vat_type === 'vat_registered' })
    : null;

  const grandTotal = scpwdCalc ? scpwdCalc.netDue : Math.max(0, subtotal - discountAmount);

  // Smart profit guard — calculate margin for the whole receipt
  const totalCost = cart.reduce((s, c) => s + (c.effective_capital || c.cost_price || 0) * c.quantity, 0);
  const margin = grandTotal - totalCost;
  const marginPercent = grandTotal > 0 ? (margin / grandTotal) * 100 : 0;
  const minMargin = salesConfig.min_margin_percent ?? 1;
  const isBelowMargin = discountAmount > 0 && marginPercent < minMargin && salesConfig.margin_warning_enabled;
  const isNegativeMargin = discountAmount > 0 && margin <= 0;

  // Camera barcode scanner
  const startScanner = async () => {
    setScannerActive(true);
    // Wait for div to render before starting scanner
    await new Promise(r => setTimeout(r, 300));
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('terminal-scanner-view');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 5, qrbox: { width: 250, height: 100 }, aspectRatio: 1.777 },
        (decodedText) => {
          // Debounce: skip if same barcode within cooldown
          const now = Date.now();
          if (decodedText === lastScanRef.current.barcode && now - lastScanRef.current.time < CAMERA_SCAN_COOLDOWN) return;
          lastScanRef.current = { barcode: decodedText, time: now };

          const product = products.find(p => p.barcode === decodedText);
          if (product) {
            addToCart(product);
            if (navigator.vibrate) navigator.vibrate(100);
          } else {
            toast.error(`No product for barcode: ${decodedText}`);
          }
        },
        () => {}
      );
    } catch (e) {
      console.error('Scanner error:', e);
      toast.error('Camera access denied. Check browser permissions.');
      setScannerActive(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      scannerRef.current = null;
    }
    setScannerActive(false);
  };

  // Capture-phase listener. It only OBSERVES keystrokes on the search field
  // (or the unfocused page) and asks the detector whether they carry a
  // scanner's fingerprint. Keys always reach the input; on a confirmed scan
  // the search box is cleared and the barcode processed. The one key ever
  // blocked is the scanner's own Enter suffix.
  useEffect(() => {
    const det = createScanDetector();
    let settleTimer = null;
    const confirm = (barcode) => {
      setSearch(''); setResults([]);
      processBarcodeScan(barcode);
    };

    const onKey = (e) => {
      if (e.key.length !== 1 && e.key !== 'Enter') return;

      // Qty modal is open — block scanner input so it doesn't type into the qty field
      if (scanQtyModalOpenRef.current) { e.preventDefault(); return; }

      // Let other inputs work normally — only watch the search field or the unfocused page
      const tag = (e.target || {}).tagName;
      if ((tag === 'INPUT' || tag === 'TEXTAREA') && e.target !== searchRef.current) return;

      // Scanner off — typing only; nothing is ever read as a scan.
      if (scannerModeRef.current === 'off') return;

      const r = det.feed(e.key, Date.now());
      clearTimeout(settleTimer);
      if (r.scan) {
        e.preventDefault();          // the suffix Enter — never a submit
        confirm(r.scan);
        return;
      }
      if (r.pending) {
        settleTimer = setTimeout(() => {
          const code = det.flush(Date.now());
          if (code) confirm(code);   // no-suffix scanner
        }, det.cfg.settleMs);
      }
    };

    window.addEventListener('keydown', onKey, true); // true = capture phase
    return () => { window.removeEventListener('keydown', onKey, true); clearTimeout(settleTimer); };
  }, [processBarcodeScan]); // eslint-disable-line

  // Process sale
  // ── Checkout state ──
  const [paymentType, setPaymentType] = useState(''); // cash, digital, credit, split
  const [amountTendered, setAmountTendered] = useState('');
  const [digitalScreenshot, setDigitalScreenshot] = useState(null);
  const [digitalRef, setDigitalRef] = useState('');
  const [creditDays, setCreditDays] = useState(15);
  const [splitCash, setSplitCash] = useState('');
  const [splitDigital, setSplitDigital] = useState('');
  const [splitScreenshot, setSplitScreenshot] = useState(null);
  const [releaseMode, setReleaseMode] = useState(''); // 'full' | 'partial'
  const fileInputRef = useRef(null);
  const splitFileInputRef = useRef(null);

  const resetCheckout = () => {
    setPaymentType(''); setAmountTendered(''); setDigitalScreenshot(null); setDigitalRef('');
    setCreditDays(15); setSplitCash(''); setSplitDigital(''); setSplitScreenshot(null); setReleaseMode('');
    setDiscountInput(''); setMarginWarningAccepted(false);
    setCropCreditConfig(null); setCropTypeDialog(false);
    // Reset pre-commit signature so the next sale starts a fresh signing session
    setPendingSigSession(null);
    setPendingOfflineBypass(null);
    saleIdRef.current = '';
    processingRef.current = false;
  };

  const changeAmount = paymentType === 'cash' && amountTendered
    ? Math.max(0, parseFloat(amountTendered) - grandTotal) : 0;

  // Generate a stable saleId once per checkout intent. Re-using the same
  // idempotency_key on retries prevents duplicate invoices when network lag
  // makes the cashier click "Confirm" twice.
  const ensureSaleId = () => {
    if (!saleIdRef.current) {
      saleIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    return saleIdRef.current;
  };

  // True when at least one cart line carries a discount.
  const cartHasDiscount = cart.some(c => (parseFloat(c.discount_value) || 0) > 0);
  // True when at least one cart line's NET unit price is at/below capital.
  // Triggers admin-PIN gate ("sell_below_capital") at checkout review.
  const cartHasBelowCost = cart.some(c => isBelowCost(c));
  // True if a sale-level (bill) discount drags any line below capital after
  // proportionally apportioning the bill discount.
  const billDiscountTriggersBelowCost = (() => {
    if (discountAmount <= 0 || subtotal <= 0) return false;
    const ratio = discountAmount / subtotal;
    return cart.some(c => {
      const cap = parseFloat(c.effective_capital ?? c.cost_price) || 0;
      if (cap <= 0) return false;
      const netUnitAfterBill = computeNetUnit(c) * (1 - ratio);
      return netUnitAfterBill <= cap;
    });
  })();
  const requiresReview = cartHasDiscount || cartHasBelowCost || discountAmount > 0 || billDiscountTriggersBelowCost;

  // Entry point for the Confirm button. Decides whether to open the Sale Review
  // modal (when discount or below-cost gates are needed) or proceed straight
  // to processSale. The review modal captures PINs and re-invokes processSale.
  const tryCheckout = async ({ sigSession = null } = {}) => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    if (requiresReview) {
      setReviewSigSession(sigSession);
      setReviewModalOpen(true);
      return;
    }
    await processSale({ sigSession });
  };

  const processSale = async ({ sigSession = null, discountPin = '', belowCapitalPin = '', priceMatch = null } = {}) => {
    if (processingRef.current) return; // hard guard against double-click re-entry
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    if (!paymentType) { toast.error('Select a payment type'); return; }
    if (!releaseMode) { toast.error('Select stock release mode'); return; }
    // Margin warning check
    if ((isBelowMargin || isNegativeMargin) && !marginWarningAccepted) {
      toast.error('Please acknowledge the low margin warning before proceeding');
      return;
    }
    if (paymentType === 'cash' && (!amountTendered || parseFloat(amountTendered) < grandTotal)) {
      toast.error('Amount tendered must be at least the total'); return;
    }
    if (paymentType === 'digital' && !digitalScreenshot) {
      toast.error('Upload a screenshot of the digital payment'); return;
    }
    if (paymentType === 'split') {
      const cashAmt = parseFloat(splitCash) || 0;
      const digAmt = parseFloat(splitDigital) || 0;
      if (cashAmt + digAmt < grandTotal) { toast.error('Cash + Digital must cover the total'); return; }
      if (digAmt > 0 && !splitScreenshot) { toast.error('Upload screenshot for the digital portion'); return; }
    }
    // Hard block: credit/partial sales cannot commit without a signature/bypass
    const requiresSignature = paymentType === 'credit' && selectedCustomer?.id;
    const sig = sigSession || pendingSigSession;
    const offlineBypass = pendingOfflineBypass;
    if (requiresSignature && isOnline && !sig?.id) {
      toast.error('Customer signature is required before recording the credit sale');
      return;
    }
    if (requiresSignature && !isOnline && !offlineBypass?.method) {
      toast.error('Manager PIN authorization is required before recording this offline credit sale');
      return;
    }
    // ── Price-match gate (owner 2026-08-10, web parity) ─────────────────────
    // Runs AFTER all payment validations so the manager PIN is asked exactly
    // once, and after the review modal (discount/below-capital PINs ride in
    // via the stash). The approval resumes processSale as an argument.
    if (!priceMatch) {
      const changes = computePriceChanges();
      if (changes.length > 0) {
        pendingCheckoutRef.current = { sigSession, discountPin, belowCapitalPin };
        setPriceMatchChanges(changes);
        setPriceMatchError('');
        setPriceMatchModal(true);
        return;
      }
    }
    processingRef.current = true;
    setSaving(true);

    const saleId = ensureSaleId();
    const envelopeId = newEnvelopeId();
    const today = localTodayStr();
    // BIR dual-date model (2026-02):
    //   - invoice_date / "Sale Date" = cashier's wall-clock today. This
    //     is the legal BIR sale date (when goods left the shelf) and is
    //     what prints on the customer's receipt. ALWAYS today.
    //   - order_date / "Record Date" = bookkeeping date. Backend uses
    //     this for the close-day guard, sales_log.date, and reports
    //     that "group by sale day". When today is already closed
    //     (cashier reopened for a late sale), we bump this to the next
    //     calendar day so the closed totals stay locked. Pure-string
    //     arithmetic — no UTC round-trip.
    const invoiceDate = today;
    const recordDate = (lastCloseDate && lastCloseDate >= today)
      ? nextCalendarDay(today)
      : today;
    const recordBumped = recordDate !== invoiceDate;

    // Last-mile confirm: only when today is closed AND we're about to
    // post-date the record. Cashiers can opt-out for the remainder of
    // this session after first confirm via `dualDateAck` below.
    if (recordBumped && !dualDateAck) {
      const ok = window.confirm(
        `Today (${today}) is closed.\n\n` +
        `• Sale Date on receipt: ${invoiceDate} (actual day sold — BIR).\n` +
        `• Record Date in books: ${recordDate} (next open business day).\n\n` +
        `Continue?`
      );
      if (!ok) {
        processingRef.current = false;
        setSaving(false);
        return;
      }
      setDualDateAck(true);
    }

    const paymentMethod = paymentType === 'cash' ? 'Cash'
      : paymentType === 'digital' ? 'Digital'
      : paymentType === 'credit' ? 'Credit'
      : paymentType === 'split' ? 'Split' : 'Cash';

    const amountPaid = paymentType === 'credit' ? 0
      : paymentType === 'split' ? parseFloat(splitCash) || 0
      : grandTotal;

    const saleItems = cart.map(c => {
      const dv = parseFloat(c.discount_value) || 0;
      const dt = c.discount_type || 'amount';
      // discount_amount = total ₱ off for the whole line
      const discAmt = dt === 'percent'
        ? round2((c.price * c.quantity) * dv / 100)
        : round2(dv * c.quantity);
      return {
        product_id: c.product_id, product_name: c.product_name, sku: c.sku,
        description: c.description || '',
        // Unit + category: the server rebuilds its own lines, but an OFFLINE
        // receipt prints from this payload — it must say "2 BAGS" and group
        // its per-category summary too (2026-09-29).
        unit: c.unit || '', category: c.category || '',
        quantity: c.quantity, rate: c.price, price: c.price, total: computeLineTotal(c),
        original_price: c.original_price,
        discount_type: dt, discount_value: dv, discount_amount: discAmt,
        is_repack: c.is_repack || false,
        vat_class: !vatEnabled ? 'vat_exempt' : (c.vat_class || 'vatable'),
      };
    });

    const saleData = {
      id: saleId, envelope_id: envelopeId, branch_id: session.branchId,
      // idempotency_key is the canonical dedupe field on the backend.
      // Stable per checkout intent — survives retries due to lag/timeout so
      // the same Confirm click never produces two invoices.
      idempotency_key: saleId,
      // Pre-commit signature: backend will back-link this session to the new invoice
      signature_session_id: sig?.id || null,
      customer_id: selectedCustomer?.id || null,
      customer_name: selectedCustomer?.name || 'Walk-in',
      items: saleItems, subtotal, freight: 0, overall_discount: discountAmount,
      scpwd: scpwd ? { enabled: true, type: scpwd.type, card_name: scpwd.card_name, card_id: scpwd.card_id, pin: scpwd.pin } : undefined,
      grand_total: grandTotal, amount_paid: amountPaid,
      tendered_amount: paymentType === 'cash' ? (parseFloat(amountTendered) || 0)
        : paymentType === 'split' ? (parseFloat(splitCash) || 0)
        : 0,
      change_amount: paymentType === 'cash' ? changeAmount : 0,
      balance: paymentType === 'credit' ? grandTotal : 0,
      terms: paymentType === 'credit' ? 'Credit' : 'COD',
      terms_days: paymentType === 'credit' ? creditDays : 0,
      prefix: 'OS',
      // BIR dual-date — sale_date is BIR-immutable, record_date can shift
      // forward when today is closed. Backend uses order_date for the
      // close-day guard / sales_log.date / reports; invoice_date is what
      // prints on the receipt as legal sale date.
      order_date: recordDate,
      invoice_date: invoiceDate,
      record_bumped_to: recordBumped ? recordDate : undefined,
      record_bumped_reason: recordBumped ? 'today_closed' : undefined,
      payment_method: paymentMethod, payment_type: paymentType,
      fund_source: 'cashier', sale_type: selectedCustomer ? 'credit' : 'walk_in',
      // Latent bug fix (2026-08-10): the terminal never sent its scheme, so
      // invoices stamped price_scheme "" and the server assumed retail even
      // for wholesale sales. Required for scheme-correct price matching.
      price_scheme: activeScheme,
      branch_name: session.branchName || undefined,
      mode: 'quick', source: 'agrismart_terminal', terminal_id: session.terminalId,
      status: paymentType === 'credit' ? 'unpaid' : 'paid',
      created_at: new Date().toISOString(),
      digital_reference: digitalRef || undefined,
      release_mode: releaseMode,
      // Approval PINs collected by the SaleReviewModal — backend re-validates
      // against the `pos_discount` and `sell_below_capital` Pin Policies.
      discount_pin: discountPin || undefined,
      below_capital_pin: belowCapitalPin || undefined,
      // Price-match approval (modal) — backend re-validates the PIN against
      // the `price_match` Pin Policy and writes branch_prices + the audit log.
      price_changes: priceMatch?.price_changes || undefined,
      price_match_pin: priceMatch?.pin || undefined,
    };

    if (paymentType === 'split') {
      saleData.split_cash = parseFloat(splitCash) || 0;
      saleData.split_digital = parseFloat(splitDigital) || 0;
    }

    // Phase 2: Offline credit sale — attach bypass metadata so /sales/sync
    // retroactively creates a signature_session(status=bypassed) on replay.
    if (offlineBypass?.method) {
      saleData.offline_bypass = {
        method: offlineBypass.method,
        by_name: offlineBypass.by_name || 'Manager',
        reason: offlineBypass.reason || '',
        at: offlineBypass.at || new Date().toISOString(),
        credit_type: cropCreditConfig?.type === 'charged_to_crop' ? 'charged_to_crop' : 'by_term',
        branch_name: session.branchName || '',
      };
    }

    if (isOnline) {
      try {
        const res = await api.post('/unified-sale', saleData);
        const invoiceNum = res.data.invoice_number || res.data.sale_number;
        invalidateBalanceCache();
        toast.success(issuesInvoiceAtCheckout ? `Sale ${invoiceNum} completed!` : `Order Slip ${invoiceNum} generated — print the order slip.`);

        // ── Crop Credit linking ──────────────────────────────────────────
        if (cropCreditConfig?.type === 'charged_to_crop' && selectedCustomer?.id && saleData.balance > 0) {
          try {
            const cropPayload = {
              amount: saleData.balance,
              invoice_id: res.data.id || '',
              invoice_number: invoiceNum || '',
              description: `Terminal credit sale ${invoiceNum}`,
              date: saleData.order_date,
            };
            if (cropCreditConfig.activeCreditId) {
              await api.post(`/crop-credits/${cropCreditConfig.activeCreditId}/add-credit`, cropPayload);
            } else {
              await api.post('/crop-credits', {
                customer_id: selectedCustomer.id,
                planting_date: cropCreditConfig.plantingDate,
                initial_amount: saleData.balance,
                invoice_id: res.data.id || '',
                invoice_number: invoiceNum || '',
                description: `Terminal credit sale ${invoiceNum}`,
                branch_id: session.branchId,
              });
            }
            if (cropCreditConfig.linkExistingTerms && cropCreditConfig.termInvoices?.length > 0) {
              const blockCheck = await api.get(`/crop-credits/check-block/${selectedCustomer.id}`);
              const creditId = blockCheck.data.active_credit_id;
              if (creditId) {
                for (const inv of cropCreditConfig.termInvoices) {
                  await api.post(`/crop-credits/${creditId}/add-credit`, {
                    amount: inv.balance, invoice_id: inv.id,
                    invoice_number: inv.invoice_number,
                    description: `Linked term: ${inv.invoice_number}`,
                    date: inv.order_date || saleData.order_date,
                  }).catch(() => {});
                }
              }
            }
          } catch { /* non-blocking */ }
          setCropCreditConfig(null);
        }

        // Store sale data for print prompt (with signature info if pre-captured)
        setLastSaleData({
          ...saleData,
          invoice_number: invoiceNum,
          ...res.data,
          signature_url: sig?.signature_url || null,
          bypass_method: sig?.bypass_method || null,
          signature_signed_at: sig?.signed_at || null,
          signature_verification_token: sig?.verification_token || null,
        });
        // Pre-commit signature flow: signature is already captured BEFORE the invoice
        // was created. Backend has linked it via signature_session_id. Go straight to print.
        if (sig?.id) {
          setShowPrintPrompt(true);
        } else if (saleData.balance > 0 && selectedCustomer?.id) {
          // Legacy fallback (shouldn't fire now — credit always pre-commits sig).
          // Kept defensively in case a future code path skips the gate.
          setTerminalSig({
            open: true,
            preCommit: false,
            invoice: {
              id: res.data.id,
              invoice_number: invoiceNum,
              customer_name: saleData.customer_name,
              customer_id: selectedCustomer.id,
              branch_id: session.branchId,
              branch_name: session.branchName || '',
              items: saleData.items,
              subtotal: saleData.subtotal,
              discount: saleData.overall_discount || 0,
              partial_paid: saleData.partial_payment || 0,
              balance: saleData.balance,
              payment_type: saleData.payment_type,
              order_date: saleData.order_date,
              credit_type: cropCreditConfig?.type === 'charged_to_crop' ? 'charged_to_crop' : 'by_term',
            },
          });
        } else {
          setShowPrintPrompt(true);
        }
        // Success — clear idempotency key + sig session so the next sale starts fresh
        saleIdRef.current = '';
        setPendingSigSession(null);
        clearCart(); setCheckoutOpen(false); resetCheckout();
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 422 && detail?.type === 'insufficient_stock') {
          setInsufficientItems(detail.items || []);
          setPendingSaleData(saleData);
          setCheckoutOpen(false);
          setStockModal(true);
          setSaving(false);
          processingRef.current = false; // allow retry via override
          return;
        }
        // Server-side price-match gate: PIN missing (422) or rejected (403).
        // Reopen the modal so the manager can retry — sale not posted.
        if ((e.response?.status === 422 && detail?.type === 'price_match_pin_required')
            || (e.response?.status === 403 && priceMatch)) {
          pendingCheckoutRef.current = { sigSession, discountPin, belowCapitalPin };
          setPriceMatchChanges(computePriceChanges());
          setPriceMatchError(
            typeof detail === 'string' ? detail
              : detail?.message || 'Manager approval required for the price change'
          );
          setPriceMatchModal(true);
          setSaving(false);
          processingRef.current = false;
          return;
        }
        if (detail && typeof detail === 'object') {
          toast.error(detail.message || 'Sale failed');
        } else {
          toast.error(typeof detail === 'string' ? detail : 'Sale failed');
        }
        setSaving(false);
        processingRef.current = false; // allow retry — same idempotency_key reused
        return;
      }
    } else {
      await addPendingSale(saleData);
      const count = await getPendingSaleCount();
      setPendingCount(count);
      toast.success('Sale saved offline — will sync when connected');
      clearCart(); setCheckoutOpen(false); resetCheckout();
    }
    setSaving(false);
    processingRef.current = false;
  };

  // Price-match modal confirm → resume the stashed checkout with the approval.
  // Offline: optimistic accept (C-1 model) — verifyOfflinePin records the
  // display verifier; the queued PIN is re-verified server-side at sync and
  // flagged for review if wrong (same pattern as offline discounts).
  const confirmPriceMatch = async ({ price_changes, pin }) => {
    setPriceMatchError('');
    if (!isOnline) {
      const v = await verifyOfflinePin(pin);
      if (!v.ok) { setPriceMatchError(v.reason || 'PIN required'); return; }
    }
    setPriceMatchModal(false);
    const args = pendingCheckoutRef.current || {};
    pendingCheckoutRef.current = null;
    await processSale({ ...args, priceMatch: { price_changes, pin } });
  };

  // Manager override for insufficient stock
  const handleStockOverride = async () => {
    if (!overridePin.trim() || !pendingSaleData) return;
    setOverrideSubmitting(true);
    setOverrideError('');
    try {
      // Reuse the same idempotency_key + signature_session from the original
      // pending sale — this is a retry of the SAME sale, just with manager auth.
      const res = await api.post('/unified-sale', {
        ...pendingSaleData,
        manager_override_pin: overridePin.trim(),
      });
      const invoiceNum = res.data.invoice_number || res.data.sale_number;
      invalidateBalanceCache();
      toast.success(`${issuesInvoiceAtCheckout ? 'Sale' : 'Order Slip'} ${invoiceNum} ${issuesInvoiceAtCheckout ? 'completed' : 'generated'} (manager override). Ticket created.`, { duration: 4000 });
      setLastSaleData({
        ...pendingSaleData,
        invoice_number: invoiceNum,
        ...res.data,
        signature_url: pendingSigSession?.signature_url || null,
        bypass_method: pendingSigSession?.bypass_method || null,
        signature_signed_at: pendingSigSession?.signed_at || null,
        signature_verification_token: pendingSigSession?.verification_token || null,
      });
      // Pre-commit signature already captured upstream — go straight to print.
      if (pendingSigSession?.id) {
        setShowPrintPrompt(true);
      } else if (pendingSaleData.balance > 0 && selectedCustomer?.id) {
        setTerminalSig({
          open: true,
          preCommit: false,
          invoice: {
            id: res.data.id,
            invoice_number: invoiceNum,
            customer_name: pendingSaleData.customer_name,
            customer_id: selectedCustomer.id,
            branch_id: session.branchId,
            branch_name: session.branchName || '',
            items: pendingSaleData.items,
            subtotal: pendingSaleData.subtotal,
            discount: pendingSaleData.overall_discount || 0,
            partial_paid: pendingSaleData.partial_payment || 0,
            balance: pendingSaleData.balance,
            payment_type: pendingSaleData.payment_type,
            order_date: pendingSaleData.order_date,
            credit_type: cropCreditConfig?.type === 'charged_to_crop' ? 'charged_to_crop' : 'by_term',
          },
        });
      } else {
        setShowPrintPrompt(true);
      }
      // Clear idempotency + sig session — sale is committed
      saleIdRef.current = '';
      setPendingSigSession(null);
      processingRef.current = false;
      setStockModal(false);
      setPendingSaleData(null);
      setInsufficientItems([]);
      setOverridePin('');
      clearCart(); resetCheckout();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setOverrideError(typeof d === 'string' ? d : d?.message || 'Invalid PIN — override denied');
    }
    setOverrideSubmitting(false);
  };

  // Helper: get stock for a product from the cached list
  const getStock = useCallback((productId) => {
    const p = products.find(pr => pr.id === productId);
    return p?.available ?? null;
  }, [products]);

  // Iter 243: If today is closed, show a persistent banner telling the cashier
  // all subsequent sales will be dated to the next business day. Not dismissable.
  const today = localTodayStr();
  const todayIsClosed = Boolean(lastCloseDate && lastCloseDate >= today);
  const nextSaleDate = todayIsClosed ? nextCalendarDay(today) : today;

  return (
    <div className="flex flex-col h-full lg:flex-row lg:overflow-hidden" data-testid="terminal-sales">
      {/* LEFT PANE — search & product list. On tablet it grows to take the
          extra width so the cashier sees more SKUs at a glance; the cart on
          the right is capped at ~440px (plenty for line items + totals +
          checkout). Phone layout (stacked) unchanged. */}
      <div className="flex flex-col lg:flex-1 lg:min-w-0 lg:h-full lg:overflow-y-auto lg:border-r lg:border-slate-200" data-testid="terminal-left-pane">
      {todayIsClosed && (
        <div
          className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-start gap-2"
          data-testid="terminal-next-day-banner"
        >
          <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs leading-snug">
            <div className="font-semibold text-amber-800">
              Today ({today}) is closed.
            </div>
            <div className="text-amber-700" data-testid="dual-date-explainer">
              • <span className="font-medium">Sale Date</span> on receipts:{' '}
              <span className="font-semibold">{today}</span>{' '}
              (actual day sold — BIR).
            </div>
            <div className="text-amber-700">
              • <span className="font-medium">Record Date</span> in books:{' '}
              <span className="font-semibold">{nextSaleDate}</span>{' '}
              (next open business day).
            </div>
          </div>
        </div>
      )}
      {/* Search Bar + Scanner Toggle */}
      <div className="p-3 bg-white border-b border-slate-200">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              ref={searchRef}
              value={search}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search product or scan barcode..."
              className="pl-9 h-10 text-base"
              data-testid="terminal-search-input"
            />
            {search && (
              <button onClick={() => { setSearch(''); setResults([]); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                <X size={16} />
              </button>
            )}
          </div>
          <Button
            variant={scannerActive ? 'default' : 'outline'}
            size="icon"
            className={`h-10 w-10 ${scannerActive ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
            onClick={scannerActive ? stopScanner : startScanner}
            data-testid="camera-scanner-btn"
          >
            <Camera size={18} />
          </Button>
          {/* Capital visibility toggle — PIN gated */}
          <Button
            variant="outline"
            size="icon"
            className={`h-10 w-10 shrink-0 ${capitalShown ? 'border-amber-400 bg-amber-50 text-amber-700' : 'text-slate-400'}`}
            onClick={() => capitalShown ? hideCapital() : setCapitalPinModal(true)}
            title={capitalShown ? 'Hide capital/cost' : 'Show capital/cost (PIN required)'}
            data-testid="capital-toggle-btn"
          >
            {capitalShown ? <EyeOff size={16} /> : <Eye size={16} />}
          </Button>
        </div>

        {/* Scanner mode chip — per device. Auto is the fixed detector; Off is
            the owner's override for a misbehaving wedge or heavy typing. */}
        <div className="mt-1.5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setScannerMode(m => (m === 'off' ? 'auto' : 'off'))}
            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              scannerMode === 'off'
                ? 'border-amber-300 bg-amber-50 text-amber-700'
                : 'border-slate-200 bg-slate-50 text-slate-500'
            }`}
            title={scannerMode === 'off'
              ? 'Typing only — keystrokes are never read as a barcode scan. Tap to turn the scanner back on.'
              : 'The search box listens for a barcode scanner. Tap to switch to typing only.'}
            data-testid="terminal-scanner-mode"
          >
            Scanner: {scannerMode === 'off' ? 'Off' : 'Auto'}
          </button>
        </div>

        {/* Camera scanner view — clipped to show only the scanning strip */}
        {scannerActive && (
          <div className="mt-2 rounded-xl overflow-hidden border border-slate-200 bg-black" ref={scannerContainerRef}
               style={{ height: 140, position: 'relative' }}>
            <div id="terminal-scanner-view" className="w-full"
                 style={{ position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)' }} />
          </div>
        )}

        {/* Search results */}
        {results.length > 0 && (
          <div className="mt-2 bg-white rounded-xl border border-slate-200 shadow-lg max-h-60 lg:max-h-[calc(100vh-230px)] overflow-auto" data-testid="search-results">
            {results.map(p => {
              const avail = p.available ?? 0;
              const isOut = avail <= 0;
              const isLow = avail > 0 && avail <= (p.reorder_point || 5);
              const isDisabled = !!p.disabled_at_branch;
              return (
              <button
                key={p.id}
                disabled={isDisabled}
                onClick={() => addToCart(p)}
                className={`w-full flex items-center justify-between px-3 py-2.5 border-b border-slate-100 last:border-0 text-left ${
                  isDisabled ? 'opacity-50 bg-slate-100/60 cursor-not-allowed'
                  : isOut ? 'bg-red-50/40' : 'hover:bg-emerald-50'
                }`}
                data-testid={`search-result-${p.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 leading-snug break-words line-clamp-2">{p.name}</p>
                  <p className="text-[11px] text-slate-400 truncate">{p.sku} {p.barcode ? `· ${p.barcode}` : ''}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="text-sm font-bold text-[#1A4D2E]">{formatPHP(getPrice(p))}</p>
                  {capitalShown && costMap[p.id] && (
                    <p className="text-[10px] text-amber-700 font-semibold" data-testid={`search-cost-${p.id}`}>
                      {/* Match /sales-new: show Cap always; MA/LP only when > 0 */}
                      Cap {formatPHP(costMap[p.id].effective_cost)}
                      {costMap[p.id].moving_average > 0 && ` · MA ${formatPHP(costMap[p.id].moving_average)}`}
                      {costMap[p.id].last_purchase > 0 && ` · LP ${formatPHP(costMap[p.id].last_purchase)}`}
                    </p>
                  )}
                  {isDisabled ? (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                      Disabled here
                    </span>
                  ) : (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      isOut ? 'bg-red-100 text-red-600' :
                      isLow ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-50 text-emerald-700'
                    }`}>
                      {isOut ? 'Out' : `${avail} ${p.unit || ''}`}
                    </span>
                  )}
                </div>
              </button>
              );
            })}
          </div>
        )}
      </div>
      </div>{/* /LEFT PANE */}

      {/* RIGHT PANE — live cart, totals & checkout. Capped at 440px on tablet
          so it doesn't swallow the screen; stacks below on phones. */}
      <div className="flex flex-col flex-1 min-h-0 lg:flex-none lg:w-[440px] lg:h-full lg:overflow-hidden lg:shrink-0" data-testid="terminal-right-pane">
      {/* Cart */}
      <div className="flex-1 overflow-auto p-3">
        {cart.length > 0 && (
          <div className="flex items-center justify-between mb-2 sticky top-0 bg-slate-50 -mx-3 px-3 py-1.5 z-10">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Discount mode</p>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setDiscountMode(m => m === 'amount' ? 'percent' : 'amount')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDiscountMode(m => m === 'amount' ? 'percent' : 'amount'); }}
              className="relative inline-flex items-center bg-slate-200 rounded-full p-0.5 cursor-pointer select-none"
              data-testid="line-discount-mode-toggle"
              aria-label={`Discount mode: ${discountMode === 'amount' ? 'Value' : 'Percent'}. Tap to switch.`}
            >
              <span className={`px-3 py-1 text-[11px] font-bold rounded-full transition-colors ${discountMode === 'amount' ? 'bg-[#1A4D2E] text-white shadow' : 'text-slate-600'}`}>
                ₱ Value
              </span>
              <span className={`px-3 py-1 text-[11px] font-bold rounded-full transition-colors ${discountMode === 'percent' ? 'bg-[#1A4D2E] text-white shadow' : 'text-slate-600'}`}>
                % Percent
              </span>
            </div>
          </div>
        )}
        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 py-12">
            <ShoppingCart size={36} className="mb-3 opacity-30" />
            <p className="text-sm">Scan or search to add items</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="cart-items">
            {cart.map(item => {
              const stock = getStock(item.product_id);
              const isOverStock = stock !== null && item.quantity > stock;
              return (
              <div key={item.product_id} className={`bg-white rounded-xl border p-3 flex flex-col gap-2 ${isOverStock ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'}`} data-testid={`cart-item-${item.product_id}`}>
                {/* Row 1: Product name (full width) + delete */}
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0 flex items-start gap-1.5">
                    <p className="text-sm font-semibold text-slate-800 leading-snug break-words flex-1" data-testid={`cart-item-name-${item.product_id}`}>{item.product_name}</p>
                    {pendingReviewIds.has(item.product_id) && session?.branch_id && (
                      <GlobalPriceBadge
                        productId={item.product_id}
                        branchId={session.branch_id}
                        reviewed={false}
                        compact
                        apiClient={api}
                        onMarked={() => {
                          setPendingReviewIds(prev => {
                            const s = new Set(prev); s.delete(item.product_id); return s;
                          });
                        }}
                      />
                    )}
                  </div>
                  <button
                    onClick={() => removeItem(item.product_id)}
                    className="text-slate-300 hover:text-red-500 p-1 flex-shrink-0"
                    data-testid={`remove-${item.product_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* Row 2: qty × price = total + Disc pill + qty controls */}
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {(() => {
                      const dv = parseFloat(item.discount_value) || 0;
                      const hasDisc = dv > 0;
                      const netUnit = computeNetUnit(item);
                      const lineTotal = computeLineTotal(item);
                      const belowCost = isBelowCost(item);
                      const priceChanged = item.original_price != null
                        && Math.abs((parseFloat(item.price) || 0) - (parseFloat(item.original_price) || 0)) > 0.001;
                      return (
                        <>
                          <p className="text-sm text-slate-700 font-mono tabular-nums" data-testid={`cart-item-line-${item.product_id}`}>
                            <span className="font-bold text-slate-800">{item.quantity}</span>
                            <span className="text-slate-400 mx-1">×</span>
                            {/* Tap the unit price to edit it (price-edit modal) */}
                            <button
                              onClick={() => openPriceEditor(item)}
                              className={`underline decoration-dotted underline-offset-2 rounded px-0.5 -mx-0.5 active:bg-slate-100 ${
                                priceChanged ? 'text-amber-700 font-semibold' : ''
                              }`}
                              data-testid={`price-edit-btn-${item.product_id}`}
                              title="Tap to edit price"
                            >
                              {hasDisc ? (
                                <>
                                  <span className="line-through text-slate-400 mr-1">{formatPHP(item.price)}</span>
                                  <span className="font-semibold text-slate-800">{formatPHP(netUnit)}</span>
                                </>
                              ) : (
                                <span>{formatPHP(item.price)}</span>
                              )}
                            </button>
                            <span className="text-slate-400 mx-1">=</span>
                            <span className={`font-bold ${belowCost ? 'text-red-600' : 'text-[#1A4D2E]'}`}>{formatPHP(lineTotal)}</span>
                          </p>
                          {priceChanged && (
                            <p className="text-[10px] text-amber-700 font-semibold mt-0.5" data-testid={`cart-item-price-changed-${item.product_id}`}>
                              {activeSchemeName} price {item.price > item.original_price ? 'increased' : 'decreased'}{' '}
                              {formatPHP(item.original_price)} → {formatPHP(item.price)} · approval at checkout
                            </p>
                          )}
                          {hasDisc && (
                            <p className="text-[10px] text-rose-600 font-medium mt-0.5" data-testid={`cart-item-discount-tag-${item.product_id}`}>
                              {item.discount_type === 'percent'
                                ? `−${dv}% off`
                                : `−${formatPHP(dv)}/unit`}
                              {' · saves '}{formatPHP(Math.max(0, item.price * item.quantity - lineTotal))}
                            </p>
                          )}
                          {belowCost && (
                            <p className="text-[10px] text-red-700 font-semibold mt-0.5" data-testid={`cart-item-below-cost-${item.product_id}`}>
                              ⚠ At/below capital — admin PIN required
                            </p>
                          )}
                          {capitalShown && costMap[item.product_id] && (
                            <p className="text-[10px] text-amber-700 font-medium mt-0.5" data-testid={`cart-cost-${item.product_id}`}>
                              {/* Cap always; MA/LP only when > 0 — same logic as /sales-new */}
                              Cap {formatPHP(costMap[item.product_id].effective_cost)}
                              {costMap[item.product_id].moving_average > 0 && ` · MA ${formatPHP(costMap[item.product_id].moving_average)}`}
                              {costMap[item.product_id].last_purchase > 0 && ` · LP ${formatPHP(costMap[item.product_id].last_purchase)}`}
                            </p>
                          )}
                          {stock !== null && (
                            <span className={`text-[10px] font-medium ${isOverStock ? 'text-amber-600' : 'text-slate-400'}`}>
                              {isOverStock ? `Only ${stock} avail` : `${stock} avail`}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => setDiscEditorFor(item.product_id === discEditorFor ? null : item.product_id)}
                      className={`h-9 px-2 rounded-lg border text-[11px] font-bold transition-colors ${
                        (parseFloat(item.discount_value) || 0) > 0
                          ? 'bg-rose-50 border-rose-300 text-rose-700 hover:bg-rose-100'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                      data-testid={`disc-toggle-${item.product_id}`}
                      title="Apply line discount"
                    >
                      {(parseFloat(item.discount_value) || 0) > 0
                        ? (item.discount_type === 'percent' ? `${item.discount_value}%` : `−${formatPHP(item.discount_value)}`)
                        : (discountMode === 'percent' ? '% Disc' : '₱ Disc')}
                    </button>
                    <button
                      onClick={() => updateQty(item.product_id, -1)}
                      className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 active:bg-slate-200"
                      data-testid={`qty-minus-${item.product_id}`}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="w-8 text-center text-sm font-bold" data-testid={`qty-display-${item.product_id}`}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQty(item.product_id, 1)}
                      className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 active:bg-slate-200"
                      data-testid={`qty-plus-${item.product_id}`}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* Row 3 (conditional): inline discount editor for this line */}
                {discEditorFor === item.product_id && (
                  <div className="border-t border-slate-100 pt-2 flex items-center gap-2" data-testid={`disc-editor-${item.product_id}`}>
                    <label className="text-[11px] text-slate-500 font-medium whitespace-nowrap">
                      {discountMode === 'percent' ? '% off each' : '₱ off / unit'}
                    </label>
                    <CalcInput
                      value={String(item.discount_value || '')}
                      onChange={(v) => setLineDiscount(item.product_id, v)}
                      placeholder={discountMode === 'percent' ? '0' : '0.00'}
                      className="h-9 flex-1 text-sm text-right"
                      data-testid={`disc-input-${item.product_id}`}
                      autoFocus
                    />
                    {(parseFloat(item.discount_value) || 0) > 0 && (
                      <button
                        onClick={() => { setLineDiscount(item.product_id, 0); }}
                        className="h-9 px-2 text-[11px] text-slate-500 hover:text-red-600"
                        data-testid={`disc-clear-${item.product_id}`}
                      >
                        Clear
                      </button>
                    )}
                    <button
                      onClick={() => setDiscEditorFor(null)}
                      className="h-9 px-3 rounded-lg bg-[#1A4D2E] text-white text-[11px] font-bold"
                      data-testid={`disc-done-${item.product_id}`}
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom: Scheme Switcher + Total + Checkout */}
      {cart.length > 0 && (
        <div className="bg-white border-t border-slate-200 p-3 safe-area-bottom">
          {/* Price scheme switcher — show all schemes, collapse if > 4 */}
          <SchemeSwitcher schemes={schemes} activeScheme={activeScheme} onSwitch={switchScheme} />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">{cart.length} item(s){discountAmount > 0 ? ` · ${formatPHP(discountAmount)} off` : ''}</p>
              <p className="text-xl font-bold text-[#1A4D2E]" data-testid="cart-total">{formatPHP(grandTotal)}</p>
              {capitalShown && cart.length > 0 && (() => {
                const mappedCost = cart.reduce((s, c) => {
                  const cost = costMap[c.product_id]?.effective_cost ?? c.effective_capital ?? c.cost_price ?? 0;
                  return s + cost * c.quantity;
                }, 0);
                return (
                  <p className="text-[10px] text-amber-700 font-medium mt-0.5"
                     data-testid="cart-capital-summary">
                    Cost {formatPHP(mappedCost)}
                  </p>
                );
              })()}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={clearCart} data-testid="clear-cart-btn">
                <Trash2 size={14} className="mr-1" /> Clear
              </Button>
              <Button
                onClick={() => setCheckoutOpen(true)}
                className="bg-[#1A4D2E] hover:bg-[#15412a] text-white px-6"
                data-testid="checkout-btn"
              >
                <Banknote size={16} className="mr-1.5" /> {issuesInvoiceAtCheckout ? 'Checkout' : 'Pay & Prepare Order'}
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>{/* /RIGHT PANE */}

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={v => { setCheckoutOpen(v); if (!v) resetCheckout(); }}>
        <DialogContent className="max-w-md mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base font-bold" style={{ fontFamily: 'Manrope' }}>
              {!paymentType ? 'Checkout' : paymentType === 'cash' ? 'Cash Payment' : paymentType === 'digital' ? 'Digital Payment' : paymentType === 'credit' ? 'Credit Sale' : 'Split Payment'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer selection */}
            <div>
              <label className="text-xs text-slate-500 font-medium mb-1 block">Customer</label>
              <Input
                value={custSearch}
                onChange={e => { setCustSearch(e.target.value); setShowCustList(true); }}
                placeholder="Walk-in (type to search)"
                className="h-9"
                data-testid="checkout-customer-input"
              />
              {showCustList && custSearch && (
                <div className="bg-white border rounded-lg mt-1 max-h-32 overflow-auto shadow-lg">
                  {customers.filter(c => c.name?.toLowerCase().includes(custSearch.toLowerCase())).slice(0, 5).map(c => (
                    <button key={c.id} onClick={() => { setSelectedCustomer(c); setCustSearch(c.name); setShowCustList(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b last:border-0">
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Order summary */}
            <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
              {cart.map(c => (
                <div key={c.product_id} className="flex justify-between text-xs">
                  <span className="text-slate-600 truncate max-w-[60%]">{c.product_name} x{c.quantity}</span>
                  <span className="text-slate-800">{formatPHP(c.total)}</span>
                </div>
              ))}
              <div className="border-t border-slate-200 pt-1.5 mt-2 flex justify-between text-xs text-slate-500">
                <span>Subtotal</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(subtotal)}</span>
              </div>

              {/* Total discount input */}
              <div className="pt-1" data-testid="discount-section">
                <label className="text-[10px] text-slate-400 font-medium mb-1 block">Discount</label>
                <div className="flex items-center gap-1.5">
                  <div className="relative flex-1">
                    <CalcInput
                      value={discountInput}
                      onChange={(v) => { setDiscountInput(v); setMarginWarningAccepted(false); }}
                      placeholder={discountMode === 'percent' ? '0%' : '₱0'}
                      className="h-8 text-sm pr-8"
                      data-testid="discount-input"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
                      {discountMode === 'percent' ? '%' : '₱'}
                    </span>
                  </div>
                  <button
                    onClick={() => { setDiscountMode(m => m === 'amount' ? 'percent' : 'amount'); setDiscountInput(''); setMarginWarningAccepted(false); }}
                    className="h-8 px-2 rounded-lg border border-slate-200 text-[10px] font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                    data-testid="discount-mode-toggle"
                  >
                    {discountMode === 'amount' ? <><Percent size={10} className="inline" /> %</> : <>₱ Amt</>}
                  </button>
                </div>
                {discountAmount > 0 && (
                  <p className="text-[10px] text-red-500 mt-0.5">-{formatPHP(discountAmount)} discount</p>
                )}
              </div>

              {/* Senior Citizen / PWD discount */}
              {scpwdAvailable && (
                <div className="pt-1" data-testid="terminal-scpwd-section">
                  {scpwd && scpwdCalc ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-2 space-y-0.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-[#1A4D2E]">{scpwd.type === 'pwd' ? 'PWD' : 'Senior'} · {scpwd.card_name}</span>
                        <button type="button" onClick={() => setScpwd(null)} data-testid="terminal-scpwd-remove" className="text-[10px] text-red-500 font-medium">Remove</button>
                      </div>
                      <div className="flex justify-between text-[10px] text-slate-500"><span>ID: {scpwd.card_id}</span><button type="button" className="underline" onClick={() => setScpwdModalOpen(true)} data-testid="terminal-scpwd-edit">Edit</button></div>
                      {scpwdCalc.vatAmount > 0 && <div className="flex justify-between text-[10px] text-slate-500"><span>Less: VAT</span><span>-{formatPHP(scpwdCalc.vatAmount)}</span></div>}
                      <div className="flex justify-between text-[11px] text-[#1A4D2E] font-medium"><span>Less: {taxConfig.scpwd_discount_percent}% Disc</span><span>-{formatPHP(scpwdCalc.discount)}</span></div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setScpwdModalOpen(true)}
                      disabled={cart.length === 0}
                      data-testid="terminal-scpwd-open"
                      className="w-full h-8 rounded-lg border border-[#1A4D2E]/40 text-[#1A4D2E] text-[11px] font-medium hover:bg-emerald-50 transition-colors disabled:opacity-50">
                      Senior Citizen / PWD Discount
                    </button>
                  )}
                </div>
              )}

              {/* Grand total */}
              <div className="border-t border-slate-200 pt-1.5 flex justify-between font-bold text-sm">
                <span>Total</span>
                <span className="text-[#1A4D2E]">{formatPHP(grandTotal)}</span>
              </div>

              {/* Smart Profit Guard — inline status only (no button here) */}
              {discountAmount > 0 && (
                <div className={`mt-1.5 p-2 rounded-lg text-xs ${
                  isNegativeMargin ? 'bg-red-50 border border-red-200' :
                  isBelowMargin ? 'bg-amber-50 border border-amber-200' :
                  'bg-emerald-50 border border-emerald-200'
                }`} data-testid="margin-guard">
                  {isNegativeMargin ? (
                    <p className="font-semibold text-red-700 flex items-center gap-1">
                      <AlertTriangle size={12} /> Loss — Cost: {formatPHP(totalCost)} · Revenue: {formatPHP(grandTotal)}
                    </p>
                  ) : isBelowMargin ? (
                    <p className="font-semibold text-amber-700 flex items-center gap-1">
                      <AlertTriangle size={12} /> Low margin: {marginPercent.toFixed(1)}% (min {minMargin}%)
                      {marginWarningAccepted && <span className="ml-1 text-amber-600 font-normal flex items-center gap-0.5"><Check size={10} /> Acknowledged</span>}
                    </p>
                  ) : (
                    <p className="text-emerald-700 flex items-center gap-1">
                      <Check size={12} /> Healthy margin: {formatPHP(margin)} ({marginPercent.toFixed(1)}%)
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Margin Acknowledgment Blocker — shown OUTSIDE the summary box, full-width, always visible */}
            {(isBelowMargin || isNegativeMargin) && !marginWarningAccepted && (
              <div className={`rounded-xl border-2 p-4 text-center ${isNegativeMargin ? 'bg-red-50 border-red-400' : 'bg-amber-50 border-amber-400'}`}
                data-testid="margin-acknowledge-panel">
                <AlertTriangle size={28} className={`mx-auto mb-2 ${isNegativeMargin ? 'text-red-500' : 'text-amber-500'}`} />
                <p className={`font-bold text-sm mb-1 ${isNegativeMargin ? 'text-red-700' : 'text-amber-700'}`}>
                  {isNegativeMargin ? 'Selling at a Loss!' : 'Low Profit Margin Warning'}
                </p>
                <p className={`text-xs mb-3 ${isNegativeMargin ? 'text-red-600' : 'text-amber-600'}`}>
                  {isNegativeMargin
                    ? `Cost ₱${totalCost.toFixed(2)} > Revenue ₱${grandTotal.toFixed(2)}. Loss of ${formatPHP(Math.abs(margin))}.`
                    : `Margin is ${marginPercent.toFixed(1)}%, below the ${minMargin}% threshold.`}
                </p>
                <button
                  onClick={() => setMarginWarningAccepted(true)}
                  className={`w-full py-3 rounded-xl font-bold text-white text-sm transition-colors active:scale-95 ${isNegativeMargin ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}`}
                  data-testid="margin-warning-accept"
                >
                  I Understand — Proceed Anyway
                </button>
              </div>
            )}

            {/* Payment Type Selection */}
            {!paymentType && (
              <div className="space-y-2" data-testid="payment-type-selection">
                <label className="text-xs text-slate-500 font-medium block">Payment Method</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setPaymentType('cash')}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
                    data-testid="pay-type-cash">
                    <Banknote size={22} className="text-emerald-600" />
                    <span className="text-sm font-medium text-slate-700">Cash</span>
                  </button>
                  <button onClick={() => setPaymentType('digital')}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    data-testid="pay-type-digital">
                    <Wallet size={22} className="text-blue-600" />
                    <span className="text-sm font-medium text-slate-700">Digital</span>
                  </button>
                  <button onClick={() => setPaymentType('split')}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors"
                    data-testid="pay-type-split">
                    <CreditCard size={22} className="text-amber-600" />
                    <span className="text-sm font-medium text-slate-700">Split</span>
                  </button>
                  <button onClick={() => { if (!selectedCustomer) { toast.error('Select a customer for credit sales'); return; } setPaymentType('credit'); }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-slate-200 hover:border-red-400 hover:bg-red-50 transition-colors"
                    data-testid="pay-type-credit">
                    <Clock size={22} className="text-red-600" />
                    <span className="text-sm font-medium text-slate-700">Credit</span>
                  </button>
                </div>
              </div>
            )}

            {/* ── Cash Payment ── */}
            {paymentType === 'cash' && (
              <div className="space-y-3" data-testid="cash-payment-form">
                <div>
                  <label className="text-xs text-slate-500 font-medium mb-1 block">Amount Tendered</label>
                  <CalcInput value={amountTendered} onChange={setAmountTendered}
                    placeholder={formatPHP(grandTotal)} h-12 text-xl text-center font-bold
                    data-testid="amount-tendered-input" autoFocus />
                </div>
                {amountTendered && parseFloat(amountTendered) >= grandTotal && (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <span className="text-sm text-emerald-700 font-medium">Change</span>
                    <span className="text-xl font-bold text-emerald-700" data-testid="change-amount">{formatPHP(changeAmount)}</span>
                  </div>
                )}
                {/* Quick amount buttons */}
                <div className="flex gap-2 flex-wrap">
                  {[grandTotal, Math.ceil(grandTotal / 100) * 100, Math.ceil(grandTotal / 500) * 500, 1000, 2000].filter((v, i, a) => a.indexOf(v) === i && v >= grandTotal).slice(0, 4).map(amt => (
                    <button key={amt} onClick={() => setAmountTendered(String(amt))}
                      className={`px-3 py-1.5 rounded-lg text-xs  font-medium border transition-colors ${
                        parseFloat(amountTendered) === amt ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-300'
                      }`}>{formatPHP(amt)}</button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Digital Payment ── */}
            {paymentType === 'digital' && (
              <div className="space-y-3" data-testid="digital-payment-form">
                <div>
                  <label className="text-xs text-slate-500 font-medium mb-1 block">Reference # (optional)</label>
                  <Input value={digitalRef} onChange={e => setDigitalRef(e.target.value)} placeholder="e.g. GCash ref number" className="h-9" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium mb-1.5 block">Payment Screenshot <span className="text-red-500">*</span></label>
                  {digitalScreenshot ? (
                    <div className="relative rounded-xl overflow-hidden border border-emerald-300 bg-emerald-50">
                      <img src={URL.createObjectURL(digitalScreenshot)} alt="proof" className="w-full max-h-40 object-contain" />
                      <button onClick={() => setDigitalScreenshot(null)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => fileInputRef.current?.click()}
                      className="w-full p-6 border-2 border-dashed border-slate-300 rounded-xl text-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      data-testid="upload-digital-proof">
                      <Upload size={24} className="mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600 font-medium">Tap to upload screenshot</p>
                      <p className="text-xs text-slate-400 mt-0.5">GCash, Maya, bank transfer, etc.</p>
                    </button>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={e => { if (e.target.files?.[0]) setDigitalScreenshot(e.target.files[0]); }} />
                </div>
              </div>
            )}

            {/* ── Credit Sale ── */}
            {paymentType === 'credit' && (
              <div className="space-y-3" data-testid="credit-payment-form">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-xs text-amber-700 font-medium">Credit sale for: <b>{selectedCustomer?.name || 'Walk-in'}</b></p>
                  <p className="text-xs text-amber-600 mt-0.5">Customer will owe {formatPHP(grandTotal)}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium mb-1.5 block">Payment Terms</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[15, 30, 60].map(d => (
                      <button key={d}
                        onClick={() => { setCreditDays(d); setCropCreditConfig(null); }}
                        className={`py-2.5 rounded-lg text-sm font-medium border transition-colors flex items-center justify-center gap-1.5 ${
                          creditDays === d && !cropCreditConfig
                            ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-300'
                        }`}
                        data-testid={`credit-days-${d}`}>
                        <Clock size={13} /> {d} days
                      </button>
                    ))}
                    <button
                      onClick={() => setCropTypeDialog(true)}
                      className={`py-2.5 rounded-lg text-sm font-medium border transition-colors flex items-center justify-center gap-1.5 ${
                        cropCreditConfig?.type === 'charged_to_crop'
                          ? 'bg-emerald-700 text-white border-emerald-700'
                          : 'bg-white border-emerald-200 text-emerald-700 hover:border-emerald-400 hover:bg-emerald-50'
                      }`}
                      data-testid="credit-type-crop">
                      <Sprout size={13} /> {cropCreditConfig?.type === 'charged_to_crop' ? 'Crop ✓' : 'Charged to Crop'}
                    </button>
                  </div>
                  {cropCreditConfig?.type === 'charged_to_crop' && (
                    <p className="text-[10px] text-emerald-700 mt-1.5 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                      Charged to Crop selected. Signature required after confirming.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Split Payment ── */}
            {paymentType === 'split' && (
              <div className="space-y-3" data-testid="split-payment-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 font-medium mb-1 block">Cash Amount</label>
                    <CalcInput value={splitCash}
                      onChange={(v) => { setSplitCash(v); setSplitDigital(String(Math.max(0, grandTotal - (parseFloat(v) || 0)))); }}
                      placeholder="0.00" h-10 data-testid="split-cash-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 font-medium mb-1 block">Digital Amount</label>
                    <CalcInput value={splitDigital}
                      onChange={(v) => { setSplitDigital(v); setSplitCash(String(Math.max(0, grandTotal - (parseFloat(v) || 0)))); }}
                      placeholder="0.00" h-10 data-testid="split-digital-input" />
                  </div>
                </div>
                {parseFloat(splitDigital) > 0 && (
                  <div>
                    <label className="text-xs text-slate-500 font-medium mb-1.5 block">Digital Payment Screenshot <span className="text-red-500">*</span></label>
                    {splitScreenshot ? (
                      <div className="relative rounded-xl overflow-hidden border border-emerald-300 bg-emerald-50">
                        <img src={URL.createObjectURL(splitScreenshot)} alt="proof" className="w-full max-h-32 object-contain" />
                        <button onClick={() => setSplitScreenshot(null)}
                          className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => splitFileInputRef.current?.click()}
                        className="w-full p-4 border-2 border-dashed border-slate-300 rounded-xl text-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        data-testid="upload-split-proof">
                        <Upload size={20} className="mx-auto text-slate-400 mb-1" />
                        <p className="text-xs text-slate-600 font-medium">Upload digital payment proof</p>
                      </button>
                    )}
                    <input ref={splitFileInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={e => { if (e.target.files?.[0]) setSplitScreenshot(e.target.files[0]); }} />
                  </div>
                )}
              </div>
            )}

            {/* Stock Release Mode — shown after payment type is selected */}
            {paymentType && !releaseMode && (
              <div className="space-y-2" data-testid="release-mode-selection">
                <label className="text-xs text-slate-500 font-medium block">Stock Release</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setReleaseMode('full')}
                    className="flex flex-col items-start p-3 rounded-xl border-2 border-emerald-400 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left"
                    data-testid="release-mode-full"
                  >
                    <span className="text-sm font-semibold text-[#1A4D2E]">Full Release</span>
                    <span className="text-[11px] text-emerald-700 mt-0.5">All items released now</span>
                  </button>
                  <button
                    onClick={() => setReleaseMode('partial')}
                    className="flex flex-col items-start p-3 rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
                    data-testid="release-mode-partial"
                  >
                    <span className="text-sm font-semibold text-amber-700">Partial Release</span>
                    <span className="text-[11px] text-amber-600 mt-0.5">Items staged for pickup</span>
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 leading-relaxed">
                  <strong>Full Release:</strong> Stock deducted immediately. Customer receives all items now.<br/>
                  <strong>Partial Release:</strong> Stock reserved. Customer scans QR code to release items in batches.
                </p>
              </div>
            )}

            {/* Action buttons */}
            {paymentType && releaseMode && (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                  <span className="text-xs text-slate-600">Release Mode:</span>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-semibold ${releaseMode === 'full' ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {releaseMode === 'full' ? 'Full Release' : 'Partial Release'}
                    </span>
                    <button
                      onClick={() => setReleaseMode('')}
                      className="text-xs text-blue-600 hover:underline"
                      data-testid="change-release-mode"
                    >
                      Change
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={resetCheckout} className="flex-1">Back</Button>
                  <Button
                    onClick={() => {
                      if (saving || processingRef.current) return; // hard double-click guard
                      if (paymentType === 'credit' && !cropCreditConfig && !creditDays) {
                        toast.error('Select payment terms (15/30/60 days or Charged to Crop)');
                        return;
                      }
                      const requiresSignature = paymentType === 'credit' && selectedCustomer?.id;
                      // ── OFFLINE PATH ─────────────────────────────────────
                      // Phase 2: When offline, skip signature_session creation
                      // (server unreachable) and collect Manager PIN bypass
                      // locally. Cached bcrypt hash verifies the PIN; sync
                      // replay creates the bypassed signature_session.
                      if (requiresSignature && !isOnline && !pendingOfflineBypass) {
                        // Local credit-limit guard (Phase 3) — best-effort
                        const lim = parseFloat(selectedCustomer?.credit_limit || 0) || 0;
                        const bal = parseFloat(selectedCustomer?.balance || 0) || 0;
                        if (lim > 0 && bal + grandTotal > lim) {
                          toast.error(`Credit limit reached (${formatPHP(lim)}). Customer balance ${formatPHP(bal)} + this sale exceeds limit.`);
                          return;
                        }
                        if (selectedCustomer?.credit_blocked_at) {
                          toast.error('This customer is credit-blocked. Cannot extend more credit offline.');
                          return;
                        }
                        setOfflineBypassDialog({
                          open: true,
                          invoice: {
                            customer_name: selectedCustomer.name,
                            balance: grandTotal,
                            credit_type: cropCreditConfig?.type === 'charged_to_crop' ? 'charged_to_crop' : 'by_term',
                            branch_name: session.branchName || '',
                            items: cart.map(c => ({
                              product_name: c.product_name, quantity: c.quantity,
                              unit: c.unit || 'unit', rate: c.price, total: c.total,
                            })),
                          },
                        });
                        return;
                      }
                      // ── ONLINE PATH ──────────────────────────────────────
                      // Credit/Partial: capture signature BEFORE creating the invoice
                      // Legal sequence: signature → invoice → print. No invoice exists
                      // until the signature/bypass session is sealed by the customer.
                      if (requiresSignature && !pendingSigSession?.id) {
                        const today = localTodayStr();
                        setTerminalSig({
                          open: true,
                          preCommit: true,
                          invoice: {
                            id: '', // no invoice yet — backend will back-link after commit
                            invoice_number: '(pending)',
                            customer_name: selectedCustomer.name,
                            customer_id: selectedCustomer.id,
                            branch_id: session.branchId,
                            branch_name: session.branchName || '',
                            items: cart.map(c => ({
                              product_name: c.product_name,
                              quantity: c.quantity,
                              unit: c.unit || 'unit',
                              rate: c.price,
                              total: c.total,
                            })),
                            subtotal,
                            discount: discountAmount,
                            partial_paid: 0,
                            balance: grandTotal,
                            payment_type: 'credit',
                            order_date: today,
                            credit_type: cropCreditConfig?.type === 'charged_to_crop' ? 'charged_to_crop' : 'by_term',
                          },
                        });
                        return;
                      }
                      tryCheckout();
                    }}
                    disabled={saving || processingRef.current}
                    className="flex-1 bg-[#1A4D2E] hover:bg-[#15412a] text-white h-12"
                    data-testid="confirm-payment-btn">
                    {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                    {saving
                      ? 'Processing...'
                      : paymentType === 'credit'
                        ? (isOnline
                            ? (pendingSigSession?.id ? 'Record Credit Sale' : 'Capture Signature')
                            : (pendingOfflineBypass ? 'Record Credit Sale' : 'Manager PIN Required'))
                        : `Pay ${formatPHP(grandTotal)}`}
                  </Button>
                </div>
              </div>
            )}

            {!isOnline && (
              <p className="text-[10px] text-amber-600 text-center">You are offline. Sale will be saved and synced when connected.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Insufficient Stock Override Modal */}
      <Dialog open={stockModal} onOpenChange={(o) => { if (!o) { setStockModal(false); setPendingSaleData(null); setInsufficientItems([]); setOverridePin(''); setOverrideError(''); } }}>

      {/* Crop Credit Type Dialog */}
      <CropCreditTypeDialog
        open={cropTypeDialog}
        onClose={() => { setCropTypeDialog(false); setCropCreditConfig(null); }}
        onConfirm={(config) => {
          setCropCreditConfig(config);
          setCropTypeDialog(false);
          tryCheckout();
        }}
        customerId={selectedCustomer?.id}
        customerName={selectedCustomer?.name || 'Customer'}
        saleAmount={grandTotal}
        branchId={session?.branchId}
        apiInstance={api}
      />
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700" style={{ fontFamily: 'Manrope' }}>
              <PackageX size={18} className="text-amber-600" /> Insufficient Stock
            </DialogTitle>
            <DialogDescription>These items have less stock than needed.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 my-1">
            {insufficientItems.map((item, i) => (
              <div key={i} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                <span className="font-medium text-slate-800 truncate max-w-[55%]">{item.product_name}</span>
                <span className="text-amber-700 text-xs">
                  Have: {item.system_qty} · Need: {item.needed_qty}
                </span>
              </div>
            ))}
          </div>

          <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/50 space-y-2">
            <div className="flex items-center gap-2">
              <ShieldAlert size={15} className="text-amber-600 shrink-0" />
              <p className="text-sm font-semibold text-amber-800">Manager Override</p>
            </div>
            <p className="text-xs text-amber-700">Proceeds with sale. Inventory goes negative and a ticket is created.</p>
            <Input
              type="password" autoComplete="one-time-code" data-lpignore="true"
              placeholder="Enter manager PIN"
              value={overridePin}
              onChange={e => { setOverridePin(e.target.value); setOverrideError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleStockOverride()}
              autoComplete="one-time-code" data-lpignore="true"
              className="h-10"
              data-testid="stock-override-pin"
            />
            {overrideError && <p className="text-xs text-red-600">{overrideError}</p>}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => { setStockModal(false); setPendingSaleData(null); setOverridePin(''); setOverrideError(''); }}
                data-testid="stock-override-cancel">
                Cancel
              </Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={handleStockOverride}
                disabled={overrideSubmitting || !overridePin.trim()} data-testid="stock-override-confirm">
                {overrideSubmitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <ShieldAlert size={14} className="mr-1.5" />}
                Override
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Order Slip Prompt */}
      <Dialog open={showPrintPrompt} onOpenChange={setShowPrintPrompt}>
        <DialogContent className="max-w-xs mx-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-base font-bold" style={{ fontFamily: 'Manrope' }}>
              {issuesInvoiceAtCheckout ? 'Sale Complete' : 'Order Slip Generated'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <Check size={28} className="text-emerald-600" />
            </div>
            <p className="text-sm text-slate-600">
              <span className="font-bold text-slate-800">{lastSaleData?.invoice_number}</span>
            </p>
            <p className="text-lg font-bold text-[#1A4D2E]">{formatPHP(lastSaleData?.grand_total || 0)}</p>
            <p className="text-xs text-slate-500">{issuesInvoiceAtCheckout ? 'Would you like to print a receipt?' : 'Would you like to print the order slip?'}</p>
            <div className="space-y-2 pt-1">
              <Button onClick={() => handleShowReceiptPreview('thermal')}
                className="w-full bg-[#1A4D2E] hover:bg-[#15412a] text-white h-11" data-testid="print-thermal-btn">
                <Printer size={16} className="mr-2" /> Print Receipt (58mm)
              </Button>
              <Button variant="outline" onClick={async () => {
                try {
                  await PrintBridge.print({ type: PrintEngine.getDocType(lastSaleData), data: lastSaleData, format: 'dot_matrix', businessInfo, docCode: lastSaleData.doc_code || lastSaleData.invoice_number || '' });
                } catch (err) { toast.error(`Print failed: ${err.message || 'Unknown error'}`); }
                setShowPrintPrompt(false); setLastSaleData(null);
              }} className="w-full h-10" data-testid="print-full-btn">
                <Printer size={14} className="mr-2" /> Print Dot Matrix
              </Button>
              {isOnline && lastSaleData?.id && (
                <Button
                  variant="outline"
                  className="w-full h-10 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => setSendToPrintOpen(true)}
                  data-testid="remote-print-btn"
                >
                  <Send size={14} className="mr-2" /> Send to Branch Printer
                </Button>
              )}
              {lastSaleData?.release_mode === 'partial' && lastSaleData?.doc_code && (
                <Button
                  variant="outline"
                  className="w-full h-10 border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => { window.open(`/doc/${lastSaleData.doc_code}`, '_blank'); }}
                  data-testid="view-release-history-btn"
                >
                  View / Manage Stock Releases
                </Button>
              )}
              {issuesInvoiceAtCheckout && lastSaleData?.id && (
                <Button
                  variant="outline"
                  className="w-full h-10 border-[#1A4D2E]/30 text-[#1A4D2E] hover:bg-[#1A4D2E]/5"
                  onClick={() => setReceiptQROpen(true)}
                  data-testid="view-receipt-qr-btn"
                >
                  <QrCode size={15} className="mr-2" /> View Receipt QR &amp; Copy Link
                </Button>
              )}
              <Button variant="ghost" onClick={() => { setShowPrintPrompt(false); setLastSaleData(null); }}
                className="w-full text-slate-500" data-testid="skip-print-btn">
                Skip
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt QR — view/copy the digital receipt link without printing */}
      <ReceiptQRDialog
        open={receiptQROpen}
        onClose={() => setReceiptQROpen(false)}
        docCode={lastSaleData?.doc_code}
        invoiceId={lastSaleData?.id}
        invoiceNumber={lastSaleData?.invoice_number}
        docType="invoice"
      />

      {/* Shared Send-to-Print modal — completed sale's stored invoice */}
      {lastSaleData?.id && (
        <SendToPrintModal
          open={sendToPrintOpen}
          onOpenChange={setSendToPrintOpen}
          compact={true}
          axiosInstance={api}
          documentType={PrintEngine.getDocType(lastSaleData)}
          receiverDocType="invoice"
          documentName={`Sales Receipt #${lastSaleData.invoice_number || ''}`}
          documentId={lastSaleData.id}
          docCode={lastSaleData.doc_code || ''}
          referenceNumber={lastSaleData.invoice_number || ''}
          branchId={lastSaleData.branch_id || session.branchId || ''}
          buildHtml={(format) => PrintEngine.generateHtml({
            type: PrintEngine.getDocType(lastSaleData),
            data: lastSaleData,
            format,
            businessInfo,
            docCode: lastSaleData.doc_code || lastSaleData.invoice_number || '',
          })}
          metadata={{ invoice_id: lastSaleData.id, customer_name: lastSaleData.customer_name || '' }}
        />
      )}

      {/* Receipt Preview — shows before actual print */}
      <Dialog open={!!receiptPreview} onOpenChange={(o) => { if (!o && !printingCopies) setReceiptPreview(null); }}>
        <DialogContent className="max-w-sm mx-auto p-0 overflow-hidden" style={{ maxHeight: '85vh' }}>
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }} data-testid="receipt-preview-title">
              <Eye size={16} /> Receipt Preview
            </DialogTitle>
            <DialogDescription className="sr-only">Preview receipt before printing</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-2" style={{ maxHeight: '55vh' }}>
            <div className="mx-auto bg-white border border-slate-200 rounded shadow-inner" style={{ width: '384px', maxWidth: '100%', transform: 'scale(0.85)', transformOrigin: 'top center' }}>
              {receiptPreview?.html && (
                <iframe
                  srcDoc={receiptPreview.html}
                  title="Receipt Preview"
                  className="w-full border-0"
                  style={{ width: '384px', minHeight: '400px', maxHeight: '600px', pointerEvents: 'none' }}
                  sandbox=""
                  data-testid="receipt-preview-iframe"
                />
              )}
            </div>
          </div>
          <div className="space-y-2 px-4 pb-4 pt-2 border-t bg-slate-50">
            <Button
              onClick={() => handlePrintCopies(1)}
              disabled={printingCopies}
              className="w-full bg-[#1A4D2E] hover:bg-[#15412a] text-white h-11"
              data-testid="print-1-copy-btn"
            >
              <Printer size={16} className="mr-2" /> {printingCopies ? 'Printing...' : 'Print 1 Copy'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handlePrintCopies(2)}
              disabled={printingCopies}
              className="w-full h-10"
              data-testid="print-2-copies-btn"
            >
              <Printer size={14} className="mr-2" /> {printingCopies ? 'Printing...' : 'Print 2 Copies'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => { setReceiptPreview(null); setShowPrintPrompt(false); setLastSaleData(null); }}
              disabled={printingCopies}
              className="w-full text-slate-500"
              data-testid="skip-preview-btn"
            >
              Skip
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Barcode Scan — Quantity Prompt */}
      <Dialog open={scanQtyModal} onOpenChange={(o) => { if (!o) { setScanQtyModal(false); setScanQtyProduct(null); setTimeout(() => searchRef.current?.focus(), 100); } }}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-base font-bold" style={{ fontFamily: 'Manrope' }} data-testid="scan-qty-title">
              Scanned Product
            </DialogTitle>
            <DialogDescription className="sr-only">Enter quantity for scanned product</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-sm font-semibold text-slate-800 leading-snug break-words" data-testid="scan-qty-product-name">{scanQtyProduct?.name}</p>
              <div className="flex items-center justify-between gap-2 mt-1 flex-wrap">
                <span className="text-lg font-bold text-[#1A4D2E]">{formatPHP(scanQtyProduct?.prices?.[activeScheme] ?? 0)}</span>
                <span className="text-[11px] text-slate-400 break-all">{scanQtyProduct?.barcode}</span>
              </div>
              {scanQtyProduct && (
                <p className="text-[10px] text-slate-500 mt-1">
                  {scanQtyProduct.available != null ? `Stock: ${scanQtyProduct.available} ${scanQtyProduct.unit || ''}` : ''}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium mb-1 block">Quantity</label>
              <CalcInput
                value={scanQty}
                onChange={setScanQty}
                onKeyDown={e => { if (e.key === 'Enter') handleScanQtyConfirm(false); }}
                className="h-12 text-xl text-center font-bold"
                data-testid="scan-qty-input"
                integerOnly
                autoFocus
              />
            </div>
            <Button
              onClick={() => handleScanQtyConfirm('add')}
              className="w-full bg-[#1A4D2E] hover:bg-[#15412a] text-white h-11"
              data-testid="scan-qty-confirm-btn"
            >
              <Check size={16} className="mr-2" /> Add to Cart
            </Button>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                onClick={() => handleScanQtyConfirm('skip_product')}
                className="w-full text-slate-600 h-10 text-xs"
                data-testid="scan-qty-skip-product-btn"
              >
                Skip — this product only (auto +1)
              </Button>
              <Button
                variant="outline"
                onClick={() => handleScanQtyConfirm('skip_all')}
                className="w-full text-amber-700 border-amber-300 hover:bg-amber-50 h-10 text-xs"
                data-testid="scan-qty-skip-all-btn"
              >
                Skip — all products this receipt (auto +1)
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 text-center -mt-1">Skip adds 1 and removes prompts for this receipt</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Scheme PIN Modal */}
      <Dialog open={schemePinModal} onOpenChange={v => { setSchemePinModal(v); if (!v) { setPendingScheme(null); setSchemePin(''); setSchemePinError(''); } }}>
        <DialogContent className="max-w-xs mx-auto">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold" style={{ fontFamily: 'Manrope' }}>
              Switch to {pendingScheme} pricing
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Enter Manager PIN, Admin PIN, or Authenticator code
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="password" inputMode="numeric" autoComplete="one-time-code" data-lpignore="true"
              value={schemePin} onChange={e => { setSchemePin(e.target.value); setSchemePinError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') confirmSchemePin(); }}
              placeholder="Enter PIN" className="h-12 text-xl font-mono text-center tracking-widest"
              autoFocus data-testid="scheme-pin-input"
            />
            {schemePinError && <p className="text-xs text-red-600">{schemePinError}</p>}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setSchemePinModal(false); setPendingScheme(null); setSchemePin(''); setSchemePinError(''); }}
                data-testid="scheme-pin-cancel">Cancel</Button>
              <Button className="flex-1 bg-[#1A4D2E] hover:bg-[#15412a] text-white" onClick={confirmSchemePin}
                disabled={schemePinLoading || !schemePin.trim()} data-testid="scheme-pin-confirm">
                {schemePinLoading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Tag size={14} className="mr-1.5" />}
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Offline credit-sale bypass: when no internet, manager PIN authorizes
          credit/partial sales (no signature capture possible). Sync replay
          creates a bypassed signature_session for audit. */}
      <OfflineCreditBypassDialog
        open={offlineBypassDialog.open}
        onOpenChange={(o) => setOfflineBypassDialog(prev => ({ ...prev, open: o }))}
        invoice={offlineBypassDialog.invoice}
        onConfirm={(meta) => {
          setPendingOfflineBypass(meta);
          setOfflineBypassDialog({ open: false, invoice: null });
          // Defer to next tick so state settles, then commit (queues to pending_sales)
          setTimeout(() => tryCheckout(), 0);
        }}
      />

      {/* Mandatory inline signature for credit/partial sales (terminal mode) */}
      <RequestSignatureDialog
        open={terminalSig.open}
        onOpenChange={(open) => {
          if (!open && terminalSig.preCommit && !pendingSigSession?.id) {
            // Q3a: Hard block — pre-commit signature was not completed.
            // No invoice has been created yet; just close and warn the cashier.
            // Cart is preserved so they can re-confirm and try again.
            toast.warning('Signature required to record this credit sale');
            setTerminalSig({ open: false, invoice: null, preCommit: false });
            return;
          }
          setTerminalSig(prev => ({ ...prev, open }));
          if (!open && !terminalSig.preCommit && lastSaleData) setShowPrintPrompt(true);
        }}
        invoice={terminalSig.invoice}
        mode="inline"
        apiInstance={api}
        onSigned={(sess) => {
          if (terminalSig.preCommit) {
            // ── Pre-commit flow: signature captured BEFORE invoice exists ──
            // 1. Persist session info so processSale can attach signature_session_id
            // 2. Close dialog
            // 3. Trigger commit → backend creates invoice and back-links the session
            const sigData = {
              id: sess.id,
              signature_url: sess.signature_url || null,
              bypass_method: sess.bypass_method || null,
              signed_at: sess.signed_at || sess.bypassed_at || null,
              verification_token: sess.verification_token || null,
            };
            setPendingSigSession(sigData);
            setTerminalSig({ open: false, invoice: null, preCommit: false });
            // Defer to next tick so state settles, then commit
            setTimeout(() => tryCheckout({ sigSession: sigData }), 0);
            return;
          }
          // ── Legacy post-commit flow (kept for safety): invoice already exists ──
          setLastSaleData(prev => prev ? {
            ...prev,
            signature_url: sess.signature_url || null,
            bypass_method: sess.bypass_method || null,
            signature_signed_at: sess.signed_at || sess.bypassed_at || null,
            signature_verification_token: sess.verification_token || null,
          } : prev);
          setTerminalSig({ open: false, invoice: null, preCommit: false });
          setShowPrintPrompt(true);
        }}
      />

      {/* Sale Review Modal — appears when sale has discounts or below-cost lines.
          Cashier must enter the required PIN(s) before processSale runs.
          PINs are forwarded to the backend which re-validates against the
          `pos_discount` and `sell_below_capital` policies (Settings → Pin Policies). */}
      <SaleReviewModal
        open={reviewModalOpen}
        onOpenChange={setReviewModalOpen}
        cart={cart}
        subtotal={subtotal}
        billDiscountAmount={discountAmount}
        grandTotal={grandTotal}
        customer={selectedCustomer}
        paymentLabel={
          paymentType === 'cash' ? 'Cash'
          : paymentType === 'digital' ? 'Digital'
          : paymentType === 'split' ? 'Split (Cash + Digital)'
          : paymentType === 'credit' ? 'Credit / Charge'
          : ''
        }
        needsDiscountPin={cartHasDiscount || discountAmount > 0}
        needsBelowCostPin={cartHasBelowCost || billDiscountTriggersBelowCost}
        computeNetUnit={computeNetUnit}
        computeLineTotal={computeLineTotal}
        isBelowCost={isBelowCost}
        tenderedAmount={paymentType === 'cash' ? (parseFloat(amountTendered) || 0) : 0}
        changeAmount={paymentType === 'cash' ? (Math.max(0, (parseFloat(amountTendered) || 0) - grandTotal)) : 0}
        onEdit={() => { setReviewModalOpen(false); setReviewSigSession(null); }}
        onApprove={async ({ discountPin, belowCapitalPin }) => {
          setReviewModalOpen(false);
          // processSale will surface any backend PIN error via toast; we still
          // await it so the modal's "Approving..." state stays visible until
          // the sale actually finishes.
          await processSale({
            sigSession: reviewSigSession,
            discountPin,
            belowCapitalPin,
          });
          setReviewSigSession(null);
        }}
      />

      <ScpwdDiscountModal
        open={scpwdModalOpen}
        onClose={() => setScpwdModalOpen(false)}
        requirePin={true}
        taxConfig={taxConfig}
        preview={scpwdCalc || (scpwdLines.length ? computeScpwd(scpwdLines, { vatRate: taxConfig.vat_rate, scpwdPct: taxConfig.scpwd_discount_percent, isVatBranch: birSettings?.vat_type === 'vat_registered' }) : null)}
        initial={scpwd}
        onApply={(data) => { setScpwd(data); setScpwdModalOpen(false); }}
      />

      {/* ── Price Match Modal (checkout gate, shared with /sales-new) ────── */}
      <PriceMatchModal
        open={priceMatchModal}
        compact
        priceChanges={priceMatchChanges}
        schemeName={activeSchemeName}
        branchName={session?.branchName || ''}
        customerName={selectedCustomer?.name || ''}
        warmPin={capitalPin || null}
        error={priceMatchError}
        submitting={saving}
        onConfirm={confirmPriceMatch}
        onCancel={() => { setPriceMatchModal(false); pendingCheckoutRef.current = null; }}
      />

      {/* ── Line Price Edit Modal (owner 2026-08-10) ─────────────────────── */}
      <Dialog open={!!priceEditItem} onOpenChange={v => { if (!v) { setPriceEditItem(null); setPriceEditInput(''); } }}>
        <DialogContent className="max-w-sm mx-auto" data-testid="price-edit-modal">
          {priceEditItem && (() => {
            const refCosts = costMap[priceEditItem.product_id];
            const parsed = round2(parseFloat(String(priceEditInput).replace(/[,₱\s]/g, '')) || 0);
            const cap = parseFloat(priceEditItem.effective_capital ?? priceEditItem.cost_price) || 0;
            const belowCap = parsed > 0 && cap > 0 && parsed <= cap;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-base font-bold" style={{ fontFamily: 'Manrope' }}>
                    Edit {activeSchemeName} Price
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm font-semibold text-slate-800 -mt-1">{priceEditItem.product_name}</p>

                {/* Branch reference prices */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 divide-y divide-slate-200 text-sm">
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-slate-500">Current {activeSchemeName} Price</span>
                    <span className="font-mono font-semibold tabular-nums">{formatPHP(priceEditItem.original_price)}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-slate-500">Moving Average</span>
                    {capitalShown && refCosts
                      ? <span className="font-mono tabular-nums text-amber-700">{refCosts.moving_average > 0 ? formatPHP(refCosts.moving_average) : '—'}</span>
                      : <span className="text-slate-400 font-mono">••••</span>}
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-slate-500">Last Purchase</span>
                    {capitalShown && refCosts
                      ? <span className="font-mono tabular-nums text-amber-700">{refCosts.last_purchase > 0 ? formatPHP(refCosts.last_purchase) : '—'}</span>
                      : <span className="text-slate-400 font-mono">••••</span>}
                  </div>
                </div>
                {!capitalShown && (
                  <button
                    onClick={() => { setCapitalPinInput(''); setCapitalPinError(''); setCapitalPinModal(true); }}
                    className="text-xs font-semibold text-amber-700 underline underline-offset-2 self-start"
                    data-testid="price-edit-reveal-costs-btn"
                  >
                    Reveal cost references (admin/manager PIN)
                  </button>
                )}

                {/* New price */}
                <div>
                  <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">New {activeSchemeName} Price</p>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoFocus
                    value={priceEditInput}
                    onChange={e => setPriceEditInput(e.target.value.replace(/[,₱\s]/g, ''))}
                    onKeyDown={e => e.key === 'Enter' && confirmPriceEdit()}
                    className="w-full h-12 px-3 text-lg font-mono font-bold rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="price-edit-input"
                  />
                  {belowCap && (
                    <p className="text-[11px] text-red-700 font-semibold mt-1">
                      ⚠ At/below capital ({formatPHP(cap)}) — admin PIN will be required at checkout
                    </p>
                  )}
                  {priceEditItem.is_repack && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      Repack item — this change applies to this sale only (repack prices are managed in Repack Pricing).
                    </p>
                  )}
                </div>

                <p className="text-xs text-slate-500">
                  Change the {activeSchemeName.toLowerCase()} price for this product? Applies to{' '}
                  <span className="font-semibold">{session?.branchName || 'this branch'} only</span> — manager approval is collected at checkout.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 h-11" onClick={() => { setPriceEditItem(null); setPriceEditInput(''); }} data-testid="price-edit-cancel-btn">
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 h-11 bg-emerald-700 hover:bg-emerald-800"
                    onClick={confirmPriceEdit}
                    disabled={!(parsed > 0) || Math.abs(parsed - (parseFloat(priceEditItem.price) || 0)) < 0.001}
                    data-testid="price-edit-confirm-btn"
                  >
                    Change Price
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Capital / Cost PIN Modal ──────────────────────────────────────── */}
      <Dialog open={capitalPinModal} onOpenChange={v => { setCapitalPinModal(v); if (!v) { setCapitalPinInput(''); setCapitalPinError(''); } }}>
        <DialogContent className="max-w-xs mx-auto" data-testid="capital-pin-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold" style={{ fontFamily: 'Manrope' }}>
              <Eye size={16} className="text-amber-600" /> View Capital / Cost
            </DialogTitle>
            <DialogDescription className="text-xs">
              Enter Admin or Manager PIN to reveal cost prices for this session.
              Data comes from this branch's purchase history.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <Input
              ref={capitalPinRef}
              type="password"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              value={capitalPinInput}
              onChange={e => { setCapitalPinInput(e.target.value); setCapitalPinError(''); }}
              onKeyDown={e => e.key === 'Enter' && submitCapitalPin()}
              placeholder="Admin / Manager PIN"
              className="h-11 text-center font-mono text-xl tracking-widest"
              data-testid="capital-pin-input"
              autoFocus
            />
            {capitalPinError && (
              <p className="text-xs text-red-600 flex items-center gap-1.5">
                <AlertTriangle size={12} /> {capitalPinError}
              </p>
            )}
            <Button
              className="w-full bg-amber-600 hover:bg-amber-700 text-white h-10"
              onClick={submitCapitalPin}
              disabled={capitalPinLoading || !capitalPinInput}
              data-testid="capital-pin-confirm"
            >
              {capitalPinLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Eye size={14} className="mr-2" />}
              {capitalPinLoading ? 'Verifying…' : 'Reveal Capital'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <TerminalPrintExtrasSheet
        open={copiesExtrasAsk != null}
        doc={lastSaleData}
        onCancel={() => setCopiesExtrasAsk(null)}
        onConfirm={(extras) => {
          const n = copiesExtrasAsk;
          setCopiesExtrasAsk(null);
          runPrintCopies(n, extras);
        }}
      />
    </div>
  );
}
